import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BooleanInput,
  ListBase,
  Title,
  useGetOne,
  useListContext,
} from 'react-admin';
import { Form } from 'react-final-form';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  MenuItem,
  MenuItem as SelectMenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useScrollTrigger,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import BorderLeftIcon from '@material-ui/icons/BorderLeft';
import BorderRightIcon from '@material-ui/icons/BorderRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import CloseIcon from '@material-ui/icons/Close';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FilterListIcon from '@material-ui/icons/FilterList';
import InboxIcon from '@material-ui/icons/Inbox';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SearchIcon from '@material-ui/icons/Search';
import SortIcon from '@material-ui/icons/Sort';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import VisibilityIcon from '@material-ui/icons/Visibility';
import MuiPagination from '@material-ui/lab/Pagination';
import get from 'lodash/get';

import AbasNavegador from '../../AbasNavegador';
import { resolverAction } from '../Fields/ActionsField';

// largura fixa aplicada a uma coluna fixada (pinada), pra que o deslocamento (left/right)
// de cada uma seja previsível sem precisar medir a largura real renderizada no DOM.
const PIN_WIDTH = 220;
// A largura de cada lado vem de variáveis CSS definidas no wrapper da tabela
// (`larguraFixadaEsquerda`/`larguraFixadaDireita`); sem elas vale o PIN_WIDTH de sempre.
const VAR_LARGURA_ESQUERDA = `var(--largura-fixada-esquerda, ${PIN_WIDTH}px)`;
const VAR_LARGURA_DIREITA = `var(--largura-fixada-direita, ${PIN_WIDTH}px)`;
// largura assumida pra coluna de seleção (checkbox), que já é sempre a primeira e funciona
// como se estivesse fixada à esquerda — colunas pinadas à esquerda começam depois dela.
const CHECKBOX_WIDTH = 56;
// quantas colunas dá pra fixar de cada lado (cada uma soma +PIN_WIDTH de classes estáticas
// abaixo; aumentar isso significa adicionar mais entradas a `colunaFixadaEsquerdaN`/`...DireitaN`).
const MAX_COLUNAS_FIXADAS = 4;

const estilosColunaFixadaEsquerda = {};
// Variante sem o deslocamento da coluna de seleção — usada quando a tela não tem
// `bulkActions` (então não existe checkbox de seleção reservando os primeiros
// CHECKBOX_WIDTH px à esquerda). Sem isso, a 1a coluna fixada ficava com `left` deslocado
// 56px pra dentro da tabela: sobrava um vão vazio na borda esquerda e a coluna fixada
// ficava sobreposta à coluna seguinte em vez de ficar ao lado dela, já que nada mais
// reserva esse espaço no fluxo normal da tabela.
const estilosColunaFixadaEsquerdaSemSelecao = {};
const estilosColunaFixadaDireita = {};
for (let i = 0; i < MAX_COLUNAS_FIXADAS; i += 1) {
  const basesticky = {
    position: 'sticky',
    // o header do Datagrid já vem com `position: sticky; top: 0` por padrão (fixação
    // vertical) — combinado ao nosso `left` sem um ancestral com scroll vertical por
    // perto, alguns navegadores deixam de aplicar a fixação horizontal corretamente.
    // `top: auto` anula esse conflito sem afetar as células do corpo (que não tem `top`).
    top: 'auto !important',
    zIndex: 3,
    width: VAR_LARGURA_ESQUERDA,
    minWidth: VAR_LARGURA_ESQUERDA,
    maxWidth: VAR_LARGURA_ESQUERDA,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    boxShadow: '2px 0 4px -2px rgba(0,0,0,0.15)',
  };
  estilosColunaFixadaEsquerda[`colunaFixadaEsquerda${i}`] = {
    ...basesticky,
    left: `calc(${CHECKBOX_WIDTH}px + ${i} * ${VAR_LARGURA_ESQUERDA})`,
  };
  estilosColunaFixadaEsquerdaSemSelecao[
    `colunaFixadaEsquerdaSemSelecao${i}`
  ] = {
    ...basesticky,
    left: `calc(${i} * ${VAR_LARGURA_ESQUERDA})`,
  };
  estilosColunaFixadaDireita[`colunaFixadaDireita${i}`] = {
    position: 'sticky',
    top: 'auto !important',
    right: `calc(${i} * ${VAR_LARGURA_DIREITA})`,
    zIndex: 3,
    width: VAR_LARGURA_DIREITA,
    minWidth: VAR_LARGURA_DIREITA,
    maxWidth: VAR_LARGURA_DIREITA,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    boxShadow: '-2px 0 4px -2px rgba(0,0,0,0.15)',
  };
}

// `true` abaixo do breakpoint 'sm' (telas de celular). Usado só para ADICIONAR classes
// extras condicionalmente nos componentes abaixo — nunca substitui as classes/estilos do
// desktop, então a versão desktop fica inalterada independente disso.
const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
};

/**
 * Props extras pra um <Menu> no mobile: abre colado embaixo do botão/ícone que o aciona
 * (em vez do posicionamento padrão do MUI, que abre sobrepondo o próprio botão) e usa a
 * classe `menuMobile` pra deixar o texto do conteúdo (que é renderizado num Portal, fora
 * do restante da árvore) do mesmo tamanho compacto dos outros elementos da tela.
 * Sem `isMobile`, retorna `{}` — não muda nada do comportamento/visual do desktop.
 */
const obterPropsMenuMobile = (isMobile, classes) =>
  isMobile
    ? {
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        getContentAnchorEl: null,
        PaperProps: { className: classes.menuMobile },
      }
    : {};

const useStyles = makeStyles((theme) => ({
  ...estilosColunaFixadaEsquerda,
  ...estilosColunaFixadaEsquerdaSemSelecao,
  ...estilosColunaFixadaDireita,
  // `inherit` (em vez de uma cor fixa) pra célula fixada continuar opaca — precisa disso
  // pra esconder o conteúdo que rola por baixo — mas ainda repetir a cor de fundo que o
  // `rowStyle` da linha aplicou (ex: vermelho de estoque zerado/negativo em
  // Resources/ProdutosV2/List.js `postRowStyle`). Uma cor fixa (ex: branco) mascarava
  // esse destaque só nas colunas fixadas.
  colunaFixadaCelula: {
    backgroundColor: 'inherit',
  },
  // cabeçalho da tabela na cor primária do tema (o Datagrid usa `classes.headerCell`; uma tela
  // que passe o próprio `classes` no Datagrid substitui isto). Texto e seta de ordenação
  // brancos; a coluna ordenada no momento fica em vermelho claro.
  headerCell: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontWeight: 500,
    '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root:focus': {
      color: 'white !important',
    },
    '& .MuiTableSortLabel-root .MuiTableSortLabel-icon': {
      color: 'white !important',
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active, & .MuiTableSortLabel-root.MuiTableSortLabel-active:hover': {
      color: '#ff5252 !important',
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
      color: '#ff5252 !important',
    },
  },
  // texto longo numa coluna fixada quebra em mais linhas em vez de ser cortado com "...".
  colunaFixadaQuebra: {
    whiteSpace: 'normal !important',
    overflow: 'visible !important',
    textOverflow: 'clip !important',
    wordBreak: 'break-word',
  },
  // o MUI Table por padrão ocupa 100% do container e deixa o navegador encolher/quebrar o
  // texto das colunas pra caber — isso nunca gera overflow real, então a rolagem nunca
  // aparece. Aqui forçamos a tabela a ter a largura natural do conteúdo (`width: auto`,
  // sobrescrevendo o `width: 100%` padrão do MUI) e impedimos quebra de linha nas células
  // (`whiteSpace: nowrap`); se isso for mais largo que o card, o `overflowX: auto` abaixo
  // passa a rolar de verdade — e é aí que as colunas fixadas (sticky) fazem sentido.
  tabelaWrapper: {
    overflowX: 'auto',
    width: '100%',
    maxWidth: '100%',
    '& .MuiTable-root': {
      width: 'auto !important',
      minWidth: '100%',
      // o MUI usa border-collapse:collapse por padrão, e isso quebra `position: sticky`
      // em células de cabeçalho (<th>) em vários navegadores — por isso as colunas
      // fixadas funcionavam no corpo da tabela, mas não no header.
      borderCollapse: 'separate !important',
      borderSpacing: 0,
    },
    '& .MuiTableCell-root': {
      whiteSpace: 'nowrap',
    },
  },
  // no modo `modoRolagemTabela="setas"`, a rolagem por arraste/scroll continua funcionando,
  // mas escondemos a barra de rolagem nativa pra incentivar o uso das setas como navegação
  // principal (visual mais limpo).
  tabelaWrapperSemScrollbar: {
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  // cópia do header que fica presa na janela quando a página rola e o header original sai
  // de vista (ver o efeito `cabecalhoFixoRef`). Usa as mesmas classes do wrapper da tabela.
  cabecalhoFixo: {
    position: 'fixed',
    display: 'none',
    zIndex: 5,
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
  },
  grupoBotoesSetaTabela: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    boxSizing: 'border-box',
    marginLeft: 16,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
  },
  // sombra nas bordas da área que rola, avisando que há mais colunas nesse sentido.
  sombraMaisColunas: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 36,
    pointerEvents: 'none',
    zIndex: 4,
  },
  grupoBotoesSetaTabelaMobile: {
    height: 24,
    marginLeft: 8,
  },
  botaoSetaTabela: {
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: 0,
    padding: '0 6px',
  },
  botaoSetaTabelaMobile: {
    padding: '0 4px',
    '& .MuiSvgIcon-root': {
      fontSize: '0.875rem !important',
    },
  },
  valorSelecionadoOrdenacao: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  botaoDirecaoOrdenacao: {
    padding: 2,
  },
  // o ícone de abrir o select e o botão de direção já ficam embutidos no `renderValue`
  // (ver SeletorOrdenacao), então o espaço reservado pelo `seletorPorPagina` pro
  // indicador padrão (que aqui está escondido) só sobraria como espaço vazio à direita.
  seletorOrdenacao: {
    '& .MuiSelect-root': {
      paddingRight: '8px !important',
      // mesma fonte dos botões "Colunas"/"Visualização" ao lado (Button size="small").
      fontSize: '0.8125rem',
    },
  },
  // aplicado via PaperProps nos menus suspensos (Colunas, Visualizações, Mais ações...)
  // no mobile — o conteúdo do Menu é renderizado num Portal fora da árvore do componente,
  // então as classes compactas dos botões não alcançam ele; precisa de uma classe própria.
  menuMobile: {
    '& .MuiMenuItem-root, & .MuiFormControlLabel-label, & .MuiButton-root': {
      fontSize: '0.75rem !important',
    },
    '& .MuiMenuItem-root': {
      minHeight: 28,
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 10,
      paddingRight: 10,
    },
    // espaço entre o ícone e o texto das opções (ex: ícone da ação + label, no "Mais
    // ações"/"Ações") — sem isso eles ficam colados, já que são renderizados em sequência.
    '& .MuiMenuItem-root .MuiSvgIcon-root': {
      marginRight: 8,
      fontSize: '1.125rem',
    },
    '& .MuiFormControlLabel-root': {
      marginRight: 0,
      gap: 4,
    },
  },
  seletorOrdenacaoMobile: {
    height: '24px !important',
    '& .MuiSelect-root': {
      height: 24,
      boxSizing: 'border-box',
      padding: '0 6px',
      fontSize: '0.6875rem',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '0.875rem !important',
    },
    '& .MuiIconButton-root': {
      padding: 1,
    },
  },
  rootCard: {
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0',
    // o layout raiz do react-admin usa `min-width: fit-content`, ou seja, a página inteira
    // cresce pra abraçar o conteúdo mais largo em vez de conter o overflow — sem isso aqui,
    // uma tabela larga faria a página toda (incluindo o menu lateral) rolar na horizontal.
    // `overflow: hidden` faz o Card parar de propagar essa largura mínima pra cima, e o
    // `tabelaWrapper` (com `overflowX: auto`) passa a ser o único ponto de rolagem.
    overflow: 'hidden',
  },
  tabsExterno: {
    backgroundColor: '#fff',
    borderBottom: '1px solid #e2e8f0',
    borderRadius: 0,
    marginBottom: 12,
    boxShadow: 'none',
  },
  breadcrumb: {
    marginBottom: 8,
  },
  cabecalhoWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 16,
    marginBottom: 16,
  },
  cabecalhoTitulo: {
    fontWeight: 700,
    fontSize: '1.5rem',
  },
  cabecalhoLadoDireito: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  cabecalhoAcoes: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  botaoCriar: {
    borderRadius: 8,
    backgroundColor: '#2563eb',
    color: '#fff',
    textTransform: 'none',
    height: 40,
    padding: '0 16px',
    '&:hover': {
      backgroundColor: '#1d4ed8',
    },
  },
  buscaRapidaInput: {
    width: 280,
    '& .MuiOutlinedInput-root': {
      borderRadius: 4,
      backgroundColor: '#fff',
    },
  },
  cabecalhoSubtitulo: {
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  botaoPill: {
    borderRadius: 8,
    textTransform: 'none',
    border: '1px solid #e2e8f0',
    '&:hover': {
      borderColor: '#cbd5e1',
    },
  },
  botaoPillMobile: {
    padding: '4px 8px',
    minWidth: 0,
    fontSize: '0.75rem',
    '& .MuiButton-startIcon': {
      marginRight: 4,
    },
    '& .MuiButton-endIcon': {
      marginLeft: 2,
    },
  },
  divisorBotaoVisualizacoes: {
    margin: '0 8px',
  },
  tabs: {
    minHeight: 44,
  },
  tab: {
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 600,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
      gap: 6,
    },
    '&.Mui-selected': {
      backgroundColor: '#eff6ff',
      borderRadius: 8,
    },
  },
  // abas no estilo "segmentado": uma pílula cinza com a aba ativa em branco, sem a linha
  // azul do estilo padrão.
  abasSegmentadasWrapper: {
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0',
  },
  abasSegmentadas: {
    // sem corte, para a bolinha do contador passar da borda da aba (desktop).
    overflow: 'visible',
    minHeight: 40,
    display: 'inline-flex',
    backgroundColor: '#eef1f6',
    borderRadius: 12,
    padding: 4,
  },
  abasSegmentadasScroller: {
    overflow: 'visible !important',
  },
  abaSegmentada: {
    minHeight: 32,
    minWidth: 112,
    textTransform: 'none',
    fontWeight: 600,
    color: '#64748b',
    opacity: 1,
    borderRadius: 9,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
      gap: 6,
    },
    // aba ativa na cor primária do tema (a mesma do cabeçalho da tabela).
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.25)',
    },
  },
  // aba com `cor`/`icon`/`contador` (opt-in): fora de seleção fica como um botão branco
  // com borda, ícone e contador na cor dela; selecionada, preenche com a cor.
  // abas com `cor`/`icon`/`contador` (opt-in) no estilo de aba de navegador: a selecionada
  // é branca e "se funde" com o conteúdo logo abaixo (cobre a linha da base); as demais
  // ficam em cinza, apagadas, como abas ao fundo.
  abasNavegadorWrapper: {
    padding: '12px 16px 0',
  },
  abasNavegador: {
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: 0,
    minHeight: 42,
  },
  abaSegmentadaDestaque: {
    overflow: 'visible',
    minHeight: 42,
    minWidth: 130,
    margin: '0 4px 0 0',
    borderRadius: '10px 10px 0 0',
    border: '1px solid #e2e8f0',
    borderBottom: 'none',
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    boxShadow: 'none',
    // não selecionada: aspecto "apagado"; ao passar o mouse ganha um pouco de destaque.
    '& .MuiTab-wrapper': { opacity: 0.6, transition: 'opacity 0.15s' },
    '&:hover': { backgroundColor: '#f8fafc' },
    '&:hover .MuiTab-wrapper': { opacity: 1 },
    '&.Mui-selected': {
      backgroundColor: '#fff',
      color: '#0f172a',
      boxShadow: 'none',
      // desce 1px sobre a linha da base do bloco das abas: some a divisória sob a aba.
      marginBottom: -1,
      paddingBottom: 1,
      zIndex: 1,
    },
    '&.Mui-selected .MuiTab-wrapper': { opacity: 1 },
  },
  // número da aba como bolinha no canto superior direito do "card" da aba; a bolinha
  // passa um pouco da borda, por isso a aba e a área das abas não cortam o que sobra.
  abaBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    transform: 'translate(35%, -40%)',
    minWidth: 20,
    height: 20,
    padding: '0 5px',
    boxSizing: 'border-box',
    borderRadius: 10,
    border: '2px solid #fff',
    backgroundColor: 'var(--cor-aba)',
    color: '#fff',
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: '16px',
    textAlign: 'center',
    zIndex: 1,
  },
  paginacaoWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 16,
    padding: '8px 16px',
  },
  paginacaoWrapperMobile: {
    flexDirection: 'column-reverse',
    justifyContent: 'center',
    textAlign: 'center',
  },
  paginacaoTextoMobile: {
    fontSize: '0.6875rem',
  },
  semDadosWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '32px 16px',
  },
  semDadosIcone: {
    fontSize: 40,
    color: '#cbd5e1',
  },
  // === ListaCards (alternativa à tabela no mobile, modoTabelaMobile="cards") ===
  listaCardsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 12,
  },
  cardsBarraTopo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  cardsSelecionarTodosGrupo: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  cardsSelecionarTodosTexto: {
    fontSize: '0.75rem',
    color: '#475569',
    whiteSpace: 'nowrap',
  },
  cardRegistro: {
    position: 'relative',
    overflow: 'hidden',
    padding: '12px 14px 12px 18px',
    borderRadius: 12,
    border: '1px solid #eef0f2',
    boxShadow:
      '0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 6px rgba(15, 23, 42, 0.06)',
    transition: 'box-shadow 0.15s ease, transform 0.15s ease',
    '&:active': {
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
      transform: 'scale(0.997)',
    },
  },
  // faixa colorida na borda esquerda do card — só pra dar um toque visual, sem significado.
  cardFaixaDestaque: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: 5,
    background: 'linear-gradient(180deg, #2563eb, #60a5fa)',
  },
  cardCabecalho: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  cardTitulo: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.3,
    color: '#0f172a',
    wordBreak: 'break-word',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '1rem !important',
      lineHeight: 1.3,
      display: 'block',
      color: '#0f172a',
    },
  },
  cardSubtituloLinha: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: -4,
  },
  // largura igual à do checkbox compacto (cardCabecalho), pra alinhar o subtítulo
  // embaixo do início do título mesmo quando há checkbox de seleção.
  cardSubtituloEspacador: {
    width: 26,
    flexShrink: 0,
  },
  cardSubtitulo: {
    flex: 1,
    minWidth: 0,
    color: '#64748b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& .MuiTypography-root': {
      fontSize: '0.6875rem !important',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'block',
      color: '#64748b',
    },
  },
  cardBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 8,
  },
  cardBadgeChip: {
    height: 20,
    fontSize: '0.625rem',
    fontWeight: 600,
    borderRadius: 6,
    backgroundColor: '#eff6ff',
    color: '#2563eb',
    '& .MuiChip-label': {
      fontSize: '0.625rem',
      paddingLeft: 8,
      paddingRight: 8,
    },
    '& .MuiTypography-root': {
      fontSize: '0.625rem !important',
    },
  },
  cardResumoLinha: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
    marginTop: 8,
  },
  cardResumoChip: {
    height: 20,
    fontSize: '0.625rem',
    fontWeight: 600,
    borderRadius: 999,
    border: '1px solid transparent',
    '& .MuiChip-label': {
      fontSize: '0.625rem',
      paddingLeft: 8,
      paddingRight: 8,
    },
    '& .MuiTypography-root': {
      fontSize: '0.625rem !important',
    },
  },
  // dados em grade de duas colunas: rótulo pequeno em cima, valor embaixo.
  cardMetaBox: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px 12px',
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: '#f8fafc',
    border: '1px solid #eef2f7',
  },
  cardMetaLinha: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  cardMetaRotulo: {
    color: '#64748b',
    fontSize: '0.6875rem !important',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  cardMetaValor: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#0f172a',
    wordBreak: 'break-word',
    '& .MuiTypography-root': {
      fontSize: '0.875rem !important',
      fontWeight: 500,
      color: '#0f172a',
    },
  },
  // ações no rodapé do cartão, com texto (a principal ocupa a linha inteira).
  cardAcoesRodape: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
    paddingTop: 12,
    borderTop: '1px solid #eef2f7',
  },
  cardBotaoDetalhes: {
    width: '100%',
    marginTop: 10,
    padding: '5px 8px',
    minHeight: 0,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'none',
    color: '#2563eb',
    border: '1px solid #dbeafe',
    borderRadius: 8,
    backgroundColor: '#f8fafc',
    '&:hover': {
      backgroundColor: '#eff6ff',
    },
  },
  cardDetalhesBloco: {
    marginTop: 8,
    paddingTop: 10,
    borderTop: '1px dashed #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  cardDetalheItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  cardLabel: {
    color: '#94a3b8',
    fontSize: '0.6875rem !important',
    whiteSpace: 'nowrap',
  },
  cardValor: {
    fontSize: '0.75rem',
    '& .MuiTypography-root': {
      fontSize: '0.75rem !important',
    },
  },
  paginacaoControlesMobile: {
    width: '100%',
    justifyContent: 'center',
  },
  paginacaoControles: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
  },
  paginacaoItens: {
    '& .MuiPaginationItem-root': {
      height: 32,
      minWidth: 32,
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      margin: '0 2px',
    },
    '& .MuiPaginationItem-page.Mui-selected': {
      backgroundColor: '#2563eb',
      borderColor: '#2563eb',
      color: '#fff',
    },
  },
  irParaPagina: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  irParaPaginaTexto: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  irParaPaginaInput: {
    width: 32,
    '& .MuiOutlinedInput-root': {
      height: 32,
    },
    '& input': {
      textAlign: 'center',
      padding: '0 4px',
      fontSize: '0.875rem',
      // remove as setas do input number no Chrome/Safari
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      // remove as setas do input number no Firefox
      MozAppearance: 'textfield',
    },
  },
  seletorPorPagina: {
    height: 32,
    fontSize: '0.875rem',
    '& .MuiSelect-root': {
      height: 32,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      padding: '0 36px 0 8px',
    },
    '& .MuiSelect-icon': {
      marginLeft: 10,
    },
  },
  filtrosAtivosWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    padding: '12px 16px',
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    marginBottom: 12,
  },
  filtrosAtivosGrupo: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  filtrosAtivosLabel: {
    color: theme.palette.text.secondary,
    marginRight: 4,
    whiteSpace: 'nowrap',
    fontWeight: 700,
  },
  botaoFiltrosAvancados: {
    borderRadius: 8,
    whiteSpace: 'nowrap',
  },
  botaoFiltrosAvancadosMobile: {
    minWidth: 0,
    minHeight: 0,
    padding: '2px 8px',
    fontSize: '0.75rem',
    textTransform: 'none',
    '& .MuiButton-startIcon': {
      margin: 0,
      marginRight: 4,
      '& .MuiSvgIcon-root': {
        fontSize: '1rem !important',
      },
    },
    '& .MuiButton-endIcon': {
      margin: 0,
    },
  },
  chipFiltroAtivo: {
    borderRadius: 8,
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1e40af',
    fontWeight: 600,
    fontSize: '0.75rem',
    '& .MuiChip-deleteIcon': {
      color: '#60a5fa',
      '&:hover': {
        color: '#1e40af',
      },
    },
  },
  chipMaisFiltros: {
    borderRadius: 8,
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    fontWeight: 600,
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  botaoLimparTudo: {
    textTransform: 'none',
    fontWeight: 600,
    color: '#2563eb',
  },
  botaoLimparTudoMobile: {
    minWidth: 0,
    minHeight: 0,
    padding: '0 6px',
    fontSize: '0.6875rem',
  },
  toolbarSecundaria: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    padding: '8px 16px',
  },
  toolbarSecundariaGrupo: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  // neutraliza o minHeight/padding do TopToolbar do react-admin (usado em toolbarActions)
  // e o estilo padrão (maiúsculas, sem borda) do Button do react-admin, pra alinhar visualmente
  // com os botões em pílula "Colunas"/"Visualizações" ao lado.
  toolbarActionsWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    '& .MuiToolbar-root': {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      minHeight: 'auto',
      padding: 0,
    },
    '& .MuiButton-root': {
      borderRadius: 8,
      textTransform: 'none',
      border: '1px solid #e2e8f0',
      padding: '4px 10px',
      fontSize: '0.8125rem',
      margin: 0,
    },
  },
  linhaSelecao: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    width: '100%',
    gap: 8,
    padding: '10px 16px',
    backgroundColor: '#eff6ff',
    borderTop: '1px solid #bfdbfe',
    borderBottom: '1px solid #bfdbfe',
  },
  linhaSelecaoLado: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  acoesSelecionadosTexto: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  acoesSelecionadosTextoMobile: {
    fontSize: '0.75rem',
  },
  botaoLimparSelecao: {
    textTransform: 'none',
    fontWeight: 600,
    color: '#2563eb',
  },
  linhaSelecaoTextoEBotaoMobile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  botaoLimparSelecaoMobile: {
    justifyContent: 'flex-start',
    padding: 0,
    minHeight: 0,
    minWidth: 0,
    fontSize: '0.6875rem',
  },
  botaoAcaoSelecao: {
    borderRadius: 8,
    textTransform: 'none',
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
  },
  botaoAcaoSelecaoPerigo: {
    color: '#dc2626',
    borderColor: '#fecaca',
  },
  colunaSelecaoFixada: {
    position: 'sticky',
    left: 0,
    zIndex: 3,
  },
  colunaSelecaoCelula: {
    // só o padding, propositalmente sem cor/fundo — o header já recebe o
    // backgroundColor/color da tela via classes={{ headerCell }} no Datagrid;
    // aqui só garantimos que a célula da linha alinhe com o checkbox do cabeçalho.
    padding: '4px 4px 4px 16px !important',
  },
  // o checkbox "selecionar todos" usa color="primary" por padrão quando marcado, o que
  // fica invisível se o header da tela tiver fundo na mesma cor do primary/secondary do
  // tema (ex: Perfis usa o secondary do tema como fundo do header). Forçamos a cor pra
  // acompanhar a cor do texto do header (que o tema já calcula com contraste adequado).
  checkboxCabecalhoSelecao: {
    color: 'inherit !important',
    '&.Mui-checked': {
      color: 'inherit !important',
    },
  },
  // checkbox de seleção de cada linha (e o do cabeçalho, junto com `checkboxCabecalhoSelecao`)
  // — menor no mobile, acompanhando o resto da tabela mais compacta.
  checkboxTabelaCompacto: {
    '& .MuiIconButton-label .MuiSvgIcon-root': {
      color: '#2563eb',
    },
    [theme.breakpoints.down('sm')]: {
      padding: 4,
      '& .MuiSvgIcon-root': {
        fontSize: '1.125rem',
      },
    },
  },
  drawerConteudo: {
    width: 360,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    [theme.breakpoints.down('xs')]: {
      width: '85vw',
    },
    // os Inputs do react-admin/MUI (TextInput, SelectInput, AutocompleteInput dentro de
    // ReferenceInput, ...) já vêm com sua própria margem vertical (`margin="dense"` é o
    // default de vários deles) — soma com o `gap` acima e dobra o espaço entre um filtro e
    // outro. Zerando aqui, o `gap` (deste container e do `grupoSwitches`) passa a ser a
    // única fonte de espaçamento vertical entre filtros.
    '& .MuiFormControl-root': {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  // espaçamento menor entre switches (BooleanInput) consecutivos do que o `gap` do
  // `drawerConteudo` — ver `agruparFiltrosPorTipo`. Esse Box conta como 1 item só pro `gap`
  // externo, então o espaço antes/depois do bloco de switches continua o padrão.
  grupoSwitches: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    // `gap: 0` já tira o espaçamento que a gente controla, mas o MuiSwitch-root em si tem
    // ~12px de padding interno (área de toque) de cada lado, bem maior que o "trilho"
    // visível — margem negativa no FormControlLabel (o item da linha inteira) puxa os
    // switches mais pra perto um do outro sem mexer no tamanho/posição interna do switch.
    '& .MuiFormControlLabel-root': {
      marginTop: -8,
      marginBottom: -8,
    },
  },
  drawerAcoes: {
    display: 'flex',
    gap: 8,
    marginTop: 'auto',
    paddingTop: 16,
    borderTop: '1px solid #e2e8f0',
  },
  drawerBotaoLimpar: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 16px',
    borderColor: '#e2e8f0',
    color: theme.palette.text.secondary,
  },
  drawerBotaoAplicar: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 16px',
    backgroundColor: '#2563eb',
    color: '#fff',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#1d4ed8',
      boxShadow: 'none',
    },
  },
  menuColunasAcoesGlobais: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    padding: '4px 8px',
  },
  menuColunasLista: {
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  menuColunasItem: {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    '&:hover': {
      borderColor: '#cbd5e1',
      backgroundColor: '#f8fafc',
    },
  },
  menuColunasAcoesFixar: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  botaoFixarColuna: {
    padding: 4,
  },
  botaoFixarColunaAtivo: {
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    '&:hover': {
      backgroundColor: '#dbeafe',
    },
  },

  // ===== Classes mobile (só "useIsMobile" aplica, nunca substituem nada do desktop) =====
  // a tabela em si continua rolando na horizontal no mobile (sem virar lista de cards) —
  // só o que está em volta dela (cabeçalho, toolbar, filtros, paginação) se adapta.
  cabecalhoWrapperMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  cabecalhoTituloLinhaMobile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  cabecalhoLadoDireitoMobile: {
    width: '100%',
    flexWrap: 'nowrap',
  },
  cabecalhoAcoesMobile: {
    width: 'auto',
    flexShrink: 0,
  },
  botaoCriarMobile: {
    minWidth: 0,
    width: 40,
    height: 40,
    padding: 0,
    '& .MuiButton-startIcon': {
      margin: 0,
    },
  },
  buscaRapidaInputMobile: {
    width: '100%',
    flex: 1,
    minWidth: 0,
    // mesma altura e arredondamento do botão de criar ao lado (40px, 8px).
    '& .MuiOutlinedInput-root': {
      height: 40,
      borderRadius: 8,
      paddingRight: 4,
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '0.875rem',
    },
  },
  toolbarSecundariaMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 4,
    padding: '4px 12px',
  },
  toolbarSecundariaGrupoMobile: {
    width: '100%',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    justifyContent: 'space-between',
    gap: 4,
    paddingTop: 4,
    paddingBottom: 4,
    '& > *': {
      flexShrink: 0,
    },
    '& .MuiButton-root': {
      padding: '2px 6px',
      minWidth: 0,
      minHeight: 0,
      fontSize: '0.6875rem',
    },
    '& .MuiButton-startIcon': {
      marginRight: 4,
      '& .MuiSvgIcon-root': {
        fontSize: '1rem !important',
      },
    },
    '& .MuiButton-endIcon': {
      marginLeft: 2,
      '& .MuiSvgIcon-root': {
        fontSize: '1rem !important',
      },
    },
  },
  toolbarActionsWrapperMobile: {
    width: '100%',
    justifyContent: 'space-between',
    paddingTop: 4,
    paddingBottom: 4,
    '& .MuiToolbar-root': {
      flexWrap: 'wrap',
    },
  },
  // no mobile, o `.content` do Layout do react-admin zera o padding (ver breakpoint
  // `down('sm')` em react-admin/Layout), deixando tudo encostado nas bordas da tela —
  // por isso aplicamos um padding próprio aqui só nesse caso.
  listaWrapperMobile: {
    padding: '0 12px',
  },
  linhaSelecaoMobile: {
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
  },
  linhaSelecaoLadoMobile: {
    minWidth: 0,
  },
  filtrosAtivosWrapperMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 12,
    paddingRight: 12,
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    marginBottom: 8,
  },
  filtrosAtivosTopoMobile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  filtrosAtivosGrupoBotaoMobile: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  filtrosAtivosGrupoMobile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
  },
}));

const IrParaPagina = () => {
  const classes = useStyles();
  const { page, setPage, total, perPage } = useListContext();
  const totalPaginas = Math.max(1, Math.ceil((total || 0) / (perPage || 1)));
  const [valor, setValor] = useState('');

  const irParaPagina = useCallback(() => {
    const numero = parseInt(valor, 10);
    if (!numero || Number.isNaN(numero)) return;
    const paginaAlvo = Math.min(Math.max(numero, 1), totalPaginas);
    setPage(paginaAlvo);
    if (paginaAlvo !== numero) setValor(String(paginaAlvo));
  }, [valor, totalPaginas, setPage]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      irParaPagina();
    }
  };

  return (
    <Box className={classes.irParaPagina}>
      <Typography variant="body2" className={classes.irParaPaginaTexto}>
        Ir para
      </Typography>
      <TextField
        type="number"
        variant="outlined"
        size="small"
        className={classes.irParaPaginaInput}
        value={valor}
        placeholder={String(page)}
        onChange={(event) => setValor(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={irParaPagina}
        inputProps={{ min: 1, max: totalPaginas }}
      />
      <Typography variant="body2" className={classes.irParaPaginaTexto}>
        de {totalPaginas}
      </Typography>
    </Box>
  );
};

const SeletorPorPagina = ({ opcoes }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { perPage, setPerPage } = useListContext();
  return (
    <Select
      value={perPage}
      onChange={(event) => setPerPage(event.target.value)}
      variant="outlined"
      margin="dense"
      className={classes.seletorPorPagina}
      IconComponent={KeyboardArrowDownIcon}
      MenuProps={obterPropsMenuMobile(isMobile, classes)}
    >
      {opcoes.map((opcao) => (
        <SelectMenuItem key={opcao} value={opcao}>
          {opcao} por página
        </SelectMenuItem>
      ))}
    </Select>
  );
};

/**
 * Mensagem exibida no lugar da paginação quando a busca já terminou e não encontrou
 * nenhum registro (ver `semDados` em InnerTabList).
 */
const SemDados = ({ itemLabelPlural }) => {
  const classes = useStyles();
  return (
    <Box className={classes.semDadosWrapper}>
      <InboxIcon className={classes.semDadosIcone} />
      <Typography variant="body2" color="textSecondary">
        Nenhum {itemLabelPlural} encontrado
      </Typography>
    </Box>
  );
};

// MuiPagination não tem uma prop direta de "quantos quadrados no máximo" — só
// `siblingCount` (vizinhos de cada lado da página atual) e `boundaryCount` (quadrados fixos
// no início/fim). Aqui fixamos `boundaryCount=1` e derivamos `siblingCount` a partir de
// `maxPaginasVisiveis`, pra expor uma única opção mais simples de configurar.
// Fórmula inversa de "quantos números aparecem, no máximo, sem reticências":
// boundaryCount*2 (início + fim) + siblingCount*2 (vizinhos dos 2 lados) + 1 (página atual).
const BOUNDARY_COUNT_PAGINACAO = 1;
const calcularSiblingCount = (maxPaginasVisiveis) =>
  Math.max(
    0,
    Math.floor((maxPaginasVisiveis - 2 * BOUNDARY_COUNT_PAGINACAO - 1) / 2),
  );

const PaginacaoComBusca = ({
  itemLabelPlural,
  rowsPerPageOptions,
  maxPaginasVisiveis,
}) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { page, perPage, total = 0, setPage } = useListContext();
  const totalPaginas = Math.max(1, Math.ceil(total / (perPage || 1)));
  const inicio = total === 0 ? 0 : (page - 1) * perPage + 1;
  const fim = Math.min(page * perPage, total);
  const siblingCount = calcularSiblingCount(maxPaginasVisiveis);

  return (
    <Box
      className={`${classes.paginacaoWrapper} ${
        isMobile ? classes.paginacaoWrapperMobile : ''
      }`}
    >
      <Typography
        variant="body2"
        color="textSecondary"
        className={isMobile ? classes.paginacaoTextoMobile : ''}
      >
        Mostrando de {inicio} a {fim} de {total} {itemLabelPlural}
      </Typography>
      <Box
        className={`${classes.paginacaoControles} ${
          isMobile ? classes.paginacaoControlesMobile : ''
        }`}
      >
        <MuiPagination
          count={totalPaginas}
          page={page}
          onChange={(event, value) => setPage(value)}
          showFirstButton
          showLastButton
          siblingCount={siblingCount}
          boundaryCount={BOUNDARY_COUNT_PAGINACAO}
          color="primary"
          shape="rounded"
          size="small"
          className={classes.paginacaoItens}
        />
        {!isMobile && <IrParaPagina />}
        {!isMobile && <SeletorPorPagina opcoes={rowsPerPageOptions} />}
      </Box>
    </Box>
  );
};

const isFilterMatch = (tabFilter = {}, filterValues = {}) => {
  if (!tabFilter || Object.keys(tabFilter).length === 0)
    return Object.keys(filterValues || {}).length === 0;
  return Object.keys(tabFilter).every((k) => {
    const atual = (filterValues || {})[k];
    // Filtro em lista (ex.: status: ['assinado', 'enviado']): o valor volta do react-admin
    // (URL/redux) como outro array, então compara pelos itens, não pela referência.
    if (Array.isArray(tabFilter[k])) {
      return (
        Array.isArray(atual) &&
        atual.length === tabFilter[k].length &&
        tabFilter[k].every((item, indice) => item === atual[indice])
      );
    }
    return atual === tabFilter[k];
  });
};

/**
 * Monta o rótulo exibido no chip de um filtro ativo, a partir da definição em
 * `advancedFilters` (procura o elemento cujo `source` bate com a chave do filtro).
 */
const buscarLabelFiltro = (source, advancedFilters, quickFilterLabel) => {
  if (source === 'q') return quickFilterLabel;
  const definicao = advancedFilters.find((f) => f.props.source === source);
  return definicao ? definicao.props.label || source : source;
};

/**
 * Agrupa sequências consecutivas de `BooleanInput` (switch) dentro de `advancedFilters` num
 * mesmo bloco, pra poder dar um espaçamento menor entre switches do que o `gap` padrão usado
 * entre os outros tipos de filtro (TextInput, ReferenceInput, ...) — sem isso, todo filtro
 * usa o mesmo `gap` do form e uma lista longa de switches fica com espaço vertical exagerado
 * entre eles. Não reordena nada: quem decide a ordem (texto antes, switches depois, etc.) é
 * a própria lista `advancedFilters` de cada tela.
 */
const agruparFiltrosPorTipo = (advancedFilters) => {
  const grupos = [];
  advancedFilters.forEach((filtro) => {
    const ultimoGrupo = grupos[grupos.length - 1];
    if (filtro.type === BooleanInput) {
      if (ultimoGrupo?.tipo === 'switches') {
        ultimoGrupo.filtros.push(filtro);
      } else {
        grupos.push({ tipo: 'switches', filtros: [filtro] });
      }
    } else {
      grupos.push({ tipo: 'unico', filtro });
    }
  });
  return grupos;
};

/**
 * Drawer com o formulário de filtros avançados.
 *
 * advancedFilters: array de elementos de Input do react-admin (TextInput, SelectInput, etc.),
 * cada um com `source` e `label`. São renderizados dentro de um <Form> (react-final-form) que,
 * ao submeter, chama setFilters com os valores preenchidos.
 */
const DrawerFiltrosAvancados = ({
  open,
  onClose,
  advancedFilters,
  filtrosFixos = {},
}) => {
  const classes = useStyles();
  const { filterValues, setFilters, displayedFilters } = useListContext();

  const handleSubmit = (values) => {
    setFilters({ ...filtrosFixos, ...values }, displayedFilters);
    onClose();
  };

  // "Limpar" some só com os filtros avançados preenchidos à mão — os filtros fixos da tab
  // atual (`filtrosFixos`, ex: status/is_kit/is_caixa/ofertasProdutos) continuam aplicados,
  // já que não são algo que a pessoa escolheu e não devem ficar removíveis por aqui (mesma
  // regra que já esconde esses filtros do chip de "Filtros ativos").
  const handleLimpar = (form) => {
    form.reset({});
    setFilters(filtrosFixos, displayedFilters);
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Form onSubmit={handleSubmit} initialValues={filterValues}>
        {({ handleSubmit: submit, form }) => (
          <form
            onSubmit={submit}
            className={classes.drawerConteudo}
            style={{ height: '100%', padding: 0, gap: 0, overflow: 'hidden' }}
          >
            <Box
              className={classes.drawerHeader}
              style={{
                padding: '24px 24px 16px',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <Typography variant="h6">Filtros avançados</Typography>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Só os filtros rolam; "Limpar" e "Aplicar" ficam sempre à vista, no rodapé. */}
            <Box
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                padding: '16px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              {agruparFiltrosPorTipo(advancedFilters).map((grupo, index) =>
                grupo.tipo === 'switches' ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box
                    key={`switches-${index}`}
                    className={classes.grupoSwitches}
                  >
                    {grupo.filtros.map((filtro) =>
                      React.cloneElement(filtro, { key: filtro.props.source }),
                    )}
                  </Box>
                ) : (
                  React.cloneElement(grupo.filtro, {
                    key: grupo.filtro.props.source,
                  })
                ),
              )}
            </Box>

            <Box
              className={classes.drawerAcoes}
              style={{
                marginTop: 0,
                padding: '16px 24px',
                backgroundColor: '#fff',
                boxShadow: '0 -2px 6px rgba(0, 0, 0, 0.06)',
              }}
            >
              <Button
                variant="outlined"
                fullWidth
                className={classes.drawerBotaoLimpar}
                onClick={() => handleLimpar(form)}
              >
                Limpar
              </Button>
              <Button
                variant="contained"
                fullWidth
                className={classes.drawerBotaoAplicar}
                type="submit"
              >
                Aplicar filtros
              </Button>
            </Box>
          </form>
        )}
      </Form>
    </Drawer>
  );
};

/**
 * Linha com os chips de filtros atualmente aplicados + botão para abrir o drawer
 * de filtros avançados.
 */
// Nome do registro escolhido num ReferenceInput (o filtro guarda só o id).
const ValorReferencia = ({ referencia, id, optionText }) => {
  const { data } = useGetOne(referencia, id);
  if (!data) return '...';
  return typeof optionText === 'function' ? optionText(data) : data[optionText];
};

// Valor do chip de "Filtros ativos" de forma legível: nome do registro (ReferenceInput),
// rótulo da opção (SelectInput com `choices`) ou Sim/Não (switch). Sem nada disso, o valor cru.
const ValorFiltroAtivo = ({ valor, definicao }) => {
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  const { reference, choices, children } = definicao?.props || {};
  if (reference) {
    return (
      <ValorReferencia
        referencia={reference}
        id={valor}
        optionText={children?.props?.optionText || 'name'}
      />
    );
  }
  if (Array.isArray(choices)) {
    const escolha = choices.find((opcao) => opcao.id === valor);
    if (escolha) return escolha.name;
  }
  return String(valor);
};

const FiltrosAtivos = ({
  advancedFilters,
  quickFilterSource,
  quickFilterLabel,
  chavesDaTabAtiva,
  onAbrirFiltrosAvancados,
  maxFiltrosVisiveis = 6,
}) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const [mostrarTodos, setMostrarTodos] = useState(false);

  // não mostra como chip o que já é representado pela tab selecionada (ex: status: ativo) —
  // senão fica redundante, já que a tab ativa já indica isso visualmente.
  const chaves = Object.keys(filterValues || {}).filter(
    (k) =>
      filterValues[k] !== undefined &&
      filterValues[k] !== '' &&
      filterValues[k] !== null &&
      !chavesDaTabAtiva.includes(k),
  );

  const removerFiltro = (chave) => {
    const novosFiltros = { ...filterValues };
    delete novosFiltros[chave];
    setFilters(novosFiltros, displayedFilters);
  };

  const temAlgoAtivo = chaves.length > 0;

  // Mesma regra do "Limpar" do drawer de filtros avançados: os filtros da tab atual
  // (`chavesDaTabAtiva`) não são removíveis por aqui, então "Limpar tudo" só derruba o
  // resto — senão os filtros fixos (ex: status/is_kit/is_caixa/ofertasProdutos) somem
  // junto, mesmo escondidos do chip.
  const limparTudo = () => {
    const filtrosFixos = chavesDaTabAtiva.reduce((acc, chave) => {
      if (filterValues[chave] !== undefined) acc[chave] = filterValues[chave];
      return acc;
    }, {});
    setFilters(filtrosFixos, displayedFilters);
  };

  const temOverflow = chaves.length > maxFiltrosVisiveis;
  const chavesVisiveis =
    temOverflow && !mostrarTodos ? chaves.slice(0, maxFiltrosVisiveis) : chaves;
  const quantidadeOculta = chaves.length - chavesVisiveis.length;

  const chipsFiltros = (
    <>
      {!temAlgoAtivo && (
        <Typography variant="body2" color="textSecondary">
          Nenhum
        </Typography>
      )}
      {chavesVisiveis.map((chave) => (
        <Chip
          key={chave}
          label={
            <>
              {buscarLabelFiltro(chave, advancedFilters, quickFilterLabel)}:{' '}
              <ValorFiltroAtivo
                valor={filterValues[chave]}
                definicao={advancedFilters.find(
                  (f) => f.props.source === chave,
                )}
              />
            </>
          }
          onDelete={() => removerFiltro(chave)}
          size="small"
          className={classes.chipFiltroAtivo}
        />
      ))}
      {quantidadeOculta > 0 && (
        <Chip
          label={`+${quantidadeOculta} filtros`}
          deleteIcon={<ArrowDropDownIcon />}
          onDelete={() => setMostrarTodos(true)}
          onClick={() => setMostrarTodos(true)}
          size="small"
          className={classes.chipMaisFiltros}
        />
      )}
    </>
  );

  const botaoLimparTudo = temAlgoAtivo && (
    <Button
      size="small"
      className={`${classes.botaoLimparTudo} ${
        isMobile ? classes.botaoLimparTudoMobile : ''
      }`}
      onClick={limparTudo}
    >
      Limpar tudo
    </Button>
  );

  // Celular: mesmo visual da tela Minhas pendências. Em cima, "Filtros ativos" com a contagem à
  // esquerda e, à direita, limpar tudo e filtros avançados só com ícone; os valores vêm abaixo.
  if (isMobile) {
    return (
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '12px 16px',
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          marginBottom: 12,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{ gap: 8 }}
        >
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              Filtros ativos:
            </Typography>
            {temAlgoAtivo ? (
              <span
                style={{
                  minWidth: 20,
                  height: 20,
                  padding: '0 6px',
                  boxSizing: 'border-box',
                  borderRadius: 10,
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  lineHeight: '20px',
                  textAlign: 'center',
                }}
              >
                {chaves.length}
              </span>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Nenhum
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            {temAlgoAtivo && (
              <Tooltip title="Limpar todos os filtros">
                <IconButton
                  aria-label="Limpar todos os filtros"
                  size="small"
                  style={{
                    borderRadius: 8,
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                  }}
                  onClick={limparTudo}
                >
                  <ClearAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Filtros avançados">
              <IconButton
                aria-label="Filtros avançados"
                size="small"
                style={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                onClick={onAbrirFiltrosAvancados}
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {temAlgoAtivo && (
          <Box className={classes.filtrosAtivosGrupoMobile}>{chipsFiltros}</Box>
        )}
      </Box>
    );
  }

  return (
    <Box className={classes.filtrosAtivosWrapper}>
      <Box className={classes.filtrosAtivosGrupo}>
        <Typography variant="body2" className={classes.filtrosAtivosLabel}>
          Filtros ativos:
        </Typography>
        {chipsFiltros}
        {botaoLimparTudo}
      </Box>
      <Button
        variant="outlined"
        size="small"
        className={classes.botaoFiltrosAvancados}
        startIcon={<FilterListIcon />}
        endIcon={<ArrowDropDownIcon />}
        onClick={onAbrirFiltrosAvancados}
      >
        Filtros avançados
      </Button>
    </Box>
  );
};

/**
 * Extrai a lista de colunas (Fields filhos do Datagrid) a partir do elemento
 * children recebido por ListPageTabbedComBuscaPagina, para alimentar o menu "Colunas".
 */
// Prioriza `source` sobre `filho.key` de propósito: `React.Children.toArray` reescreve
// a key de cada elemento (prefixa com algo como ".0" ou ".$minhaKey"), então usar `.key`
// pra identificar a coluna de fora (ex: na prop `views`) não é confiável. `source` é estável.
const linhaOpaca = () => ({ backgroundColor: 'white' });

const obterChaveColuna = (filho, index) =>
  filho.props.source || filho.key || `coluna-${index}`;

const extrairColunas = (datagridElement) => {
  const filhos = React.Children.toArray(datagridElement?.props?.children || []);
  return filhos.map((filho, index) => ({
    key: obterChaveColuna(filho, index),
    label: filho.props.label || filho.props.source || `Coluna ${index + 1}`,
    // sortBy tem prioridade sobre source (mesma regra que o Datagrid usa pra decidir o
    // campo de ordenação de cada coluna).
    sortBy: filho.props.sortBy || filho.props.source,
    // só dá pra ordenar por uma coluna que tenha um campo de ordenação E que não tenha
    // `sortable={false}` explícito (default do Datagrid é considerar ordenável).
    sortable:
      filho.props.sortable !== false &&
      Boolean(filho.props.sortBy || filho.props.source),
  }));
};

/**
 * Select com as colunas ordenáveis (só escolhe o campo) + botão de direção (asc/desc)
 * embutido no valor exibido, usado no lugar do clique no cabeçalho quando
 * `modoOrdenacao="select"`. `colunas` já vem filtrado só com as que têm `sortBy`/`source`
 * e não têm `sortable={false}`.
 */

const SeletorOrdenacao = ({ colunas, currentSort, onChangeSort }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();

  if (!colunas.length) return null;

  const campoAtual = colunas.some((c) => c.sortBy === currentSort?.field)
    ? currentSort.field
    : colunas[0].sortBy;
  const ordemAtual = currentSort?.order === 'DESC' ? 'DESC' : 'ASC';
  const colunaAtual =
    colunas.find((c) => c.sortBy === campoAtual) || colunas[0];

  // impede que o clique no ícone de direção dispare a abertura do select (já que ele fica
  // dentro da área clicável do `renderValue`) — só troca asc/desc.
  const alternarDirecao = (event) => {
    event.stopPropagation();
    onChangeSort(campoAtual, ordemAtual === 'ASC' ? 'DESC' : 'ASC');
  };

  return (
    <Select
      value={campoAtual}
      onChange={(event) => onChangeSort(event.target.value, ordemAtual)}
      variant="outlined"
      margin="dense"
      className={`${classes.seletorPorPagina} ${classes.seletorOrdenacao} ${
        isMobile ? classes.seletorOrdenacaoMobile : ''
      }`}
      // o ícone de abrir o select aparece dentro do `renderValue`, logo depois do nome da
      // coluna — por isso o indicador padrão (que ficaria na ponta direita) é escondido.
      IconComponent={() => null}
      MenuProps={obterPropsMenuMobile(isMobile, classes)}
      renderValue={(valor) => {
        const coluna = colunas.find((c) => c.sortBy === valor) || colunaAtual;
        return (
          <Box className={classes.valorSelecionadoOrdenacao}>
            {isMobile ? (
              <SortIcon fontSize="small" />
            ) : (
              <>
                Ordenação
                <Divider orientation="vertical" flexItem />
              </>
            )}
            {coluna.label}
            <ArrowDropDownIcon fontSize="small" />
            <Divider orientation="vertical" flexItem />
            <IconButton
              size="small"
              className={classes.botaoDirecaoOrdenacao}
              onMouseDown={alternarDirecao}
            >
              {ordemAtual === 'ASC' ? (
                <ArrowUpwardIcon fontSize="small" />
              ) : (
                <ArrowDownwardIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        );
      }}
    >
      {colunas.map((coluna, index) => [
        index > 0 && <Divider key={`${coluna.key}-divisor`} />,
        <SelectMenuItem key={coluna.key} value={coluna.sortBy}>
          {coluna.label}
        </SelectMenuItem>,
      ])}
    </Select>
  );
};

/**
 * Botão + menu para mostrar/ocultar colunas do Datagrid filho.
 */
const MenuColunas = ({
  colunas,
  colunasVisiveis,
  onToggleColuna,
  onMarcarTodas,
  colunasFixadas,
  onFixarColuna,
  onDesfixarTodas,
  permitirFixar = true,
}) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState(null);
  const temAlgumaFixada = Object.keys(colunasFixadas).length > 0;

  return (
    <>
      <Button
        size="small"
        startIcon={<ViewColumnIcon />}
        endIcon={isMobile ? undefined : <ArrowDropDownIcon />}
        className={`${classes.botaoPill} ${
          isMobile ? classes.botaoPillMobile : ''
        }`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        Colunas
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        {...obterPropsMenuMobile(isMobile, classes)}
      >
        <Box className={classes.menuColunasAcoesGlobais}>
          <Button size="small" onClick={onMarcarTodas}>
            Marcar todas
          </Button>
          {permitirFixar && (
            <Button
              size="small"
              disabled={!temAlgumaFixada}
              onClick={onDesfixarTodas}
            >
              Desfixar todas
            </Button>
          )}
        </Box>
        <Divider />
        <Box className={classes.menuColunasLista}>
          {colunas.map((coluna) => {
            const fixacao = colunasFixadas[coluna.key];
            return (
              <MenuItem
                key={coluna.key}
                dense
                className={classes.menuColunasItem}
                onClick={() => onToggleColuna(coluna.key)}
              >
                <FormControlLabel
                  onClick={(event) => event.stopPropagation()}
                  control={
                    <Checkbox
                      size="small"
                      checked={colunasVisiveis[coluna.key] !== false}
                      onChange={() => onToggleColuna(coluna.key)}
                    />
                  }
                  label={coluna.label}
                />
                {permitirFixar && (
                  <Box className={classes.menuColunasAcoesFixar}>
                    <Tooltip title="Fixar à esquerda">
                      <IconButton
                        size="small"
                        className={`${classes.botaoFixarColuna} ${
                          fixacao === 'esquerda'
                            ? classes.botaoFixarColunaAtivo
                            : ''
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onFixarColuna(coluna.key, 'esquerda');
                        }}
                      >
                        <BorderLeftIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Fixar à direita">
                      <IconButton
                        size="small"
                        className={`${classes.botaoFixarColuna} ${
                          fixacao === 'direita'
                            ? classes.botaoFixarColunaAtivo
                            : ''
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onFixarColuna(coluna.key, 'direita');
                        }}
                      >
                        <BorderRightIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </>
  );
};

/**
 * Calcula o mapa inicial de colunas visíveis (mesmo formato usado por `colunasVisiveis`)
 * a partir da view selecionada. Se a view não definir `colunasVisiveis`, todas as colunas
 * ficam visíveis por padrão (mapa vazio == "tudo visível").
 */
const construirVisibilidadeInicial = (view, colunas) => {
  if (!view || !Array.isArray(view.colunasVisiveis)) return {};
  const permitidas = new Set(view.colunasVisiveis);
  const mapa = {};
  colunas.forEach((coluna) => {
    mapa[coluna.key] = permitidas.has(coluna.key);
  });
  return mapa;
};

/**
 * Filtra o array `actions` de um campo do tipo ActionsField (ou qualquer Field com prop
 * `actions`) conforme a lista de keys permitidas pela view atual. Sem efeito em campos
 * que não tenham `actions`, ou quando a view não restringe ações (`acoesPermitidas` nulo).
 *
 * Identifica cada ação pelo `key` (se a ação não tiver, cai pro `title` — mas prefira
 * sempre definir `key`, já que `title` pode mudar/ser traduzido e quebrar a view).
 */
const aplicarFiltroDeAcoes = (filho, acoesPermitidas) => {
  if (!acoesPermitidas || !Array.isArray(filho.props.actions)) return filho;
  return React.cloneElement(filho, {
    actions: filho.props.actions.filter((acao) =>
      acoesPermitidas.includes(acao.key || acao.title),
    ),
  });
};

// `mobile` (config de papel pro card, ver `modoTabelaMobile="cards"`) é só lido por este
// componente — os Fields do react-admin (TextField, FunctionField, etc.) não conhecem essa
// prop e a repassariam pra um <span>/<Typography> real, gerando um warning de atributo DOM
// desconhecido. Removemos antes de qualquer Field chegar a ser efetivamente renderizado
// (tanto na tabela quanto nos cards).
const omitirPropMobile = (filho) =>
  filho.props.mobile !== undefined
    ? React.cloneElement(filho, { mobile: undefined })
    : filho;

// nome dos campos que, sem configuração explícita, o fallback abaixo já reconhece como
// "summary" (badge/status curto) ou "meta" (datas) — só usado quando o Field não declarar
// seu próprio `mobile.role`.
const CAMPOS_SUMMARY_PADRAO = ['status', 'categoria', 'tipo', 'origem'];
const CAMPOS_META_PADRAO = /data|created|updated|ultimo/i;

/**
 * Resolve o "papel" de cada campo no card mobile (ver doc de `modoTabelaMobile="cards"`):
 * usa `mobile.role` quando o Field declarar explicitamente (ex:
 * `<TextField source="nome" mobile={{ role: 'title' }} />` — `mobile` é só um prop extra
 * que o react-admin ignora normalmente, então é seguro declarar em qualquer Field). Sem
 * isso, infere uma posição razoável pela ordem/nome do campo, pra nenhuma tela "quebrar"
 * só por não ter configurado nada.
 *
 * Papéis: title, subtitle, summary (linha curta, várias por card, separadas por "·"),
 * meta (linha "rótulo: valor"), badge (chip curto), detail (só aparece em "Ver detalhes"),
 * hidden (não aparece no card), action (vira o menu de ações no topo do card).
 */
const resolverPapelMobile = (filhos) =>
  filhos.map((filho, index) => {
    const papelExplicito = filho.props.mobile?.role;
    const chave = obterChaveColuna(filho, index);
    if (papelExplicito) return { filho, chave, papel: papelExplicito };
    // 'acoes' é a convenção já usada nas telas que reaproveitam este componente pro
    // campo de ações — sem isso, ele cairia em "detail" e o menu de ações "desapareceria"
    // dentro do "Ver detalhes".
    if (chave === 'acoes') return { filho, chave, papel: 'action' };
    if (index === 0) return { filho, chave, papel: 'title' };
    if (index === 1) return { filho, chave, papel: 'subtitle' };
    const fonte = filho.props.source || '';
    if (CAMPOS_SUMMARY_PADRAO.includes(fonte)) {
      return { filho, chave, papel: 'summary' };
    }
    if (CAMPOS_META_PADRAO.test(fonte)) return { filho, chave, papel: 'meta' };
    return { filho, chave, papel: 'detail' };
  });

/**
 * Renderiza o valor de um campo pro card: se o Field tiver `mobile.format` (função
 * `(valorBruto) => string`), usa ela em vez do próprio Field — útil pra um texto mais
 * curto/diferente no card do que o que aparece na tabela. Sem isso, clona o Field como
 * sempre (preserva ícones/cores/formatação que ele já tiver).
 */
const COR_RESUMO_PADRAO = '#059669';

// cor do chip de summary: `mobile.color` pode ser uma cor fixa (string) ou uma função
// `(valorBruto, record) => cor`, pra colorir diferente conforme o valor (ex: status).
const resolverCorResumo = (filho, record) => {
  const corConfig = filho.props.mobile?.color;
  if (typeof corConfig === 'function') {
    return (
      corConfig(get(record, filho.props.source), record) || COR_RESUMO_PADRAO
    );
  }
  return typeof corConfig === 'string' ? corConfig : COR_RESUMO_PADRAO;
};

const renderizarValorCampo = (filho, record, basePath, resource) => {
  const formatar = filho.props.mobile?.format;
  if (typeof formatar === 'function') {
    return formatar(get(record, filho.props.source));
  }
  return React.cloneElement(filho, {
    record,
    basePath: filho.props.basePath || basePath,
    resource,
    mobile: undefined,
  });
};

// Ações do cartão no celular: as duas primeiras viram botões lado a lado (a primeira em destaque)
// e o resto fica num menu "mais ações". Ação desabilitada ou escondida não entra (só serve para
// alinhar as colunas da tabela). `rotuloCurto` da ação, se houver, é o texto do botão.
const AcoesDoCard = ({ acoes, record }) => {
  const classes = useStyles();
  const [ancora, setAncora] = useState(null);
  const itens = acoes
    .map((acao, idx) => ({ acao, idx, ...resolverAction(acao, record) }))
    .filter(({ show, disabled }) => show && !disabled);
  if (itens.length === 0) return null;
  const principais = itens.slice(0, 2);
  const resto = itens.slice(2);
  const colunas = [
    principais.length === 1 ? '1fr' : '1fr 1fr',
    resto.length > 0 ? '44px' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      className={classes.cardAcoesRodape}
      style={{ display: 'grid', gridTemplateColumns: colunas, gap: 8 }}
    >
      {principais.map(({ acao, idx, iconNode, title }, posicao) => (
        <Button
          key={acao.key || idx}
          variant={posicao === 0 ? 'contained' : 'outlined'}
          color="primary"
          startIcon={iconNode}
          onClick={() => acao.onClick(record)}
          style={{ minHeight: 44, minWidth: 0, textTransform: 'none' }}
        >
          {acao.rotuloCurto || title}
        </Button>
      ))}
      {resto.length > 0 && (
        <>
          <IconButton
            aria-label="Mais ações"
            style={{
              width: 44,
              height: 44,
              borderRadius: 4,
              border: '1px solid rgba(37, 99, 235, 0.5)',
              color: '#2563eb',
            }}
            onClick={(e) => setAncora(e.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={ancora}
            open={Boolean(ancora)}
            onClose={() => setAncora(null)}
          >
            {resto.map(({ acao, idx, iconNode, title }) => (
              <MenuItem
                key={acao.key || idx}
                onClick={() => {
                  setAncora(null);
                  acao.onClick(record);
                }}
              >
                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                  {iconNode}
                  {acao.rotuloCurto || title}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
};

/**
 * Alternativa à tabela pra mobile (`modoTabelaMobile="cards"`): em vez de linhas/colunas
 * com rolagem horizontal, cada registro vira um card, organizado pelo "papel" de cada
 * campo (ver `resolverPapelMobile`) — título e subtítulo em destaque, resumo numa linha
 * curta, metadados como "rótulo: valor", e o resto escondido atrás de "Ver detalhes".
 *
 * `datagridElement` é o mesmo elemento `<Datagrid>` recebido como `children` por
 * ListPageTabbedComBuscaPagina — usamos só os Fields filhos dele, igual ao que a tabela
 * normal faz; não rendeiramos o Datagrid em si (ele monta uma <table>, que não serve aqui).
 */
const ListaCards = ({
  datagridElement,
  colunasVisiveis,
  acoesPermitidas,
  bulkActions,
  selecao,
  modoOrdenacao,
  colunasOrdenaveis,
  currentSort,
  onChangeSort,
  renderCard,
}) => {
  const classes = useStyles();
  const { ids = [], data = {}, basePath, resource } = useListContext();
  const [expandidos, setExpandidos] = useState(() => new Set());

  const alternarExpandido = (id) => {
    setExpandidos((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const filhos = React.Children.toArray(datagridElement?.props?.children || []);
  const filhosVisiveis = filhos
    .filter((filho, index) => {
      const chave = obterChaveColuna(filho, index);
      return colunasVisiveis[chave] !== false;
    })
    .map((filho) => aplicarFiltroDeAcoes(filho, acoesPermitidas));

  const camposComPapel = resolverPapelMobile(filhosVisiveis);
  const campoTitulo = camposComPapel.find((c) => c.papel === 'title');
  const campoSubtitulo = camposComPapel.find((c) => c.papel === 'subtitle');
  const camposResumo = camposComPapel.filter((c) => c.papel === 'summary');
  const camposBadge = camposComPapel.filter((c) => c.papel === 'badge');
  const camposMeta = camposComPapel.filter((c) => c.papel === 'meta');
  const camposDetalhe = camposComPapel.filter((c) => c.papel === 'detail');
  const campoAcoesOriginal = camposComPapel.find((c) => c.papel === 'action');
  // no card, todas as ações ficam atrás do menu de 3 pontinhos (sem botões visíveis
  // soltos) — `maxAcoesVisiveis={0}` força isso no ActionsField, independente do limite
  // configurado pra tabela (ex: `maxAcoesVisiveis={2}` em Perfils).
  // Com a lista de ações conhecida (ActionsField), elas viram botões com texto no rodapé do
  // cartão; sem ela, continua o menu de 3 pontinhos no topo.
  const acoesDoCard =
    campoAcoesOriginal && Array.isArray(campoAcoesOriginal.filho.props.actions)
      ? campoAcoesOriginal.filho.props.actions
      : null;
  const campoAcoes =
    campoAcoesOriginal && !acoesDoCard
      ? {
          ...campoAcoesOriginal,
          filho: React.cloneElement(campoAcoesOriginal.filho, {
            maxAcoesVisiveis: 0,
          }),
        }
      : null;
  const temSelecao = bulkActions.length > 0;
  // título e subtítulo aparecem truncados no cabeçalho do card; o "Ver detalhes" deve
  // existir mesmo sem nenhum campo `detail` configurado, pra dar acesso ao texto completo.
  const temDetalhes =
    camposDetalhe.length > 0 || !!campoTitulo || !!campoSubtitulo;

  return (
    <Box className={classes.listaCardsWrapper}>
      {(temSelecao || modoOrdenacao === 'select') && (
        <Box className={classes.cardsBarraTopo}>
          {temSelecao ? (
            <Box className={classes.cardsSelecionarTodosGrupo}>
              <Checkbox
                color="primary"
                className={classes.checkboxTabelaCompacto}
                checked={selecao.tudoSelecionado}
                indeterminate={
                  selecao.algumSelecionado && !selecao.tudoSelecionado
                }
                onClick={selecao.alternarSelecaoTudo}
              />
              <Typography
                variant="body2"
                className={classes.cardsSelecionarTodosTexto}
              >
                Selecionar todos
              </Typography>
            </Box>
          ) : (
            <Box />
          )}
          {modoOrdenacao === 'select' && (
            <SeletorOrdenacao
              colunas={colunasOrdenaveis}
              currentSort={currentSort}
              onChangeSort={onChangeSort}
            />
          )}
        </Box>
      )}
      {ids.map((id) => {
        const record = data[id];
        if (!record) return null;
        const expandido = expandidos.has(id);

        // Tela com cartão próprio: ela desenha o cartão e recebe as ações já resolvidas.
        if (renderCard) {
          return (
            <Box key={id}>
              {renderCard(record, { acoes: acoesDoCard || [] })}
            </Box>
          );
        }

        return (
          <Card key={id} className={classes.cardRegistro}>
            <Box className={classes.cardFaixaDestaque} />
            <Box className={classes.cardCabecalho}>
              {temSelecao && (
                <Checkbox
                  color="primary"
                  className={classes.checkboxTabelaCompacto}
                  checked={selecao.estaMarcado(id)}
                  onClick={(event) => {
                    event.stopPropagation();
                    selecao.alternarSelecao(id);
                  }}
                />
              )}
              {campoTitulo && (
                <Box className={classes.cardTitulo}>
                  {renderizarValorCampo(
                    campoTitulo.filho,
                    record,
                    basePath,
                    resource,
                  )}
                </Box>
              )}
              {campoAcoes &&
                renderizarValorCampo(
                  campoAcoes.filho,
                  record,
                  basePath,
                  resource,
                )}
            </Box>
            {campoSubtitulo && (
              <Box className={classes.cardSubtituloLinha}>
                {temSelecao && (
                  <Box className={classes.cardSubtituloEspacador} />
                )}
                <Box className={classes.cardSubtitulo}>
                  {renderizarValorCampo(
                    campoSubtitulo.filho,
                    record,
                    basePath,
                    resource,
                  )}
                </Box>
              </Box>
            )}

            {camposBadge.length > 0 && (
              <Box className={classes.cardBadges}>
                {camposBadge.map((c) => (
                  <Chip
                    key={c.chave}
                    size="small"
                    label={renderizarValorCampo(
                      c.filho,
                      record,
                      basePath,
                      resource,
                    )}
                    className={classes.cardBadgeChip}
                  />
                ))}
              </Box>
            )}

            {camposResumo.length > 0 && (
              <Box className={classes.cardResumoLinha}>
                {camposResumo.map((c) => {
                  const cor = resolverCorResumo(c.filho, record);
                  return (
                    <Chip
                      key={c.chave}
                      size="small"
                      label={renderizarValorCampo(
                        c.filho,
                        record,
                        basePath,
                        resource,
                      )}
                      className={classes.cardResumoChip}
                      style={{
                        backgroundColor: `${cor}1A`,
                        borderColor: `${cor}66`,
                        color: cor,
                      }}
                    />
                  );
                })}
              </Box>
            )}

            {camposMeta.length > 0 && (
              <Box className={classes.cardMetaBox}>
                {camposMeta.map((c) => (
                  <Box key={c.chave} className={classes.cardMetaLinha}>
                    <Typography
                      variant="body2"
                      className={classes.cardMetaRotulo}
                    >
                      {c.filho.props.label || c.filho.props.source}
                    </Typography>
                    <Box className={classes.cardMetaValor}>
                      {renderizarValorCampo(
                        c.filho,
                        record,
                        basePath,
                        resource,
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {acoesDoCard && <AcoesDoCard acoes={acoesDoCard} record={record} />}

            {temDetalhes && (
              <>
                <Button
                  size="small"
                  className={classes.cardBotaoDetalhes}
                  endIcon={expandido ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => alternarExpandido(id)}
                >
                  {expandido ? 'Ver menos' : 'Ver detalhes'}
                </Button>
                {expandido && (
                  <Box className={classes.cardDetalhesBloco}>
                    {campoTitulo && (
                      <Box className={classes.cardDetalheItem}>
                        <Typography
                          variant="body2"
                          className={classes.cardLabel}
                        >
                          {campoTitulo.filho.props.label ||
                            campoTitulo.filho.props.source}
                        </Typography>
                        <Box className={classes.cardValor}>
                          {renderizarValorCampo(
                            campoTitulo.filho,
                            record,
                            basePath,
                            resource,
                          )}
                        </Box>
                      </Box>
                    )}
                    {campoSubtitulo && (
                      <Box className={classes.cardDetalheItem}>
                        <Typography
                          variant="body2"
                          className={classes.cardLabel}
                        >
                          {campoSubtitulo.filho.props.label ||
                            campoSubtitulo.filho.props.source}
                        </Typography>
                        <Box className={classes.cardValor}>
                          {renderizarValorCampo(
                            campoSubtitulo.filho,
                            record,
                            basePath,
                            resource,
                          )}
                        </Box>
                      </Box>
                    )}
                    {camposDetalhe.map((c) => (
                      <Box key={c.chave} className={classes.cardDetalheItem}>
                        <Typography
                          variant="body2"
                          className={classes.cardLabel}
                        >
                          {c.filho.props.label || c.filho.props.source}
                        </Typography>
                        <Box className={classes.cardValor}>
                          {renderizarValorCampo(
                            c.filho,
                            record,
                            basePath,
                            resource,
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Card>
        );
      })}
    </Box>
  );
};

/**
 * Botão + menu para trocar a "visualização" ativa. Cada view pode restringir as colunas
 * visíveis por padrão e as ações disponíveis no ActionsField (ver prop `views` do
 * ListPageTabbedComBuscaPagina).
 */
const MenuVisualizacoes = ({ views, viewAtualId, onSelecionar }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState(null);
  const viewAtual = views.find((view) => view.id === viewAtualId) || views[0];

  return (
    <>
      <Button
        size="small"
        startIcon={<VisibilityIcon />}
        endIcon={<ArrowDropDownIcon />}
        className={`${classes.botaoPill} ${
          isMobile ? classes.botaoPillMobile : ''
        }`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {!isMobile && (
          <>
            Visualização
            <Divider
              orientation="vertical"
              flexItem
              className={classes.divisorBotaoVisualizacoes}
            />
          </>
        )}
        {viewAtual?.label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        {...obterPropsMenuMobile(isMobile, classes)}
      >
        {views.map((view, index) => [
          index > 0 && <Divider key={`${view.id}-divisor`} />,
          <MenuItem
            key={view.id}
            selected={view.id === viewAtual?.id}
            onClick={() => {
              onSelecionar(view.id);
              setAnchorEl(null);
            }}
          >
            {view.label}
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
};

/**
 * Controla a seleção em massa do Datagrid com suporte a "selecionar todos os N
 * registros que batem com o filtro atual" (não só os da página carregada):
 *
 * - Modo normal: idsSelecionados guarda os ids marcados um a um (igual ao Datagrid padrão).
 * - Modo "selecionar todos": ativado ao clicar no checkbox do cabeçalho. A partir daí,
 *   todo registro é considerado marcado, EXCETO os que estiverem em idsExcecao (marcados
 *   pela pessoa ao desmarcar manualmente uma linha).
 *
 * A seleção é resetada sempre que o filtro muda, porque o conjunto "todos" passa a
 * significar outra coisa.
 */
const useSelecaoEmMassa = (total, filterValues) => {
  const [modoSelecaoTotal, setModoSelecaoTotal] = useState(false);
  const [idsExcecao, setIdsExcecao] = useState(() => new Set());
  const [idsSelecionados, setIdsSelecionados] = useState(() => new Set());
  const filtroChave = JSON.stringify(filterValues || {});

  const limparSelecao = useCallback(() => {
    setModoSelecaoTotal(false);
    setIdsSelecionados(new Set());
    setIdsExcecao(new Set());
  }, []);

  useEffect(() => {
    limparSelecao();
  }, [filtroChave, limparSelecao]);

  const estaMarcado = useCallback(
    (id) => (modoSelecaoTotal ? !idsExcecao.has(id) : idsSelecionados.has(id)),
    [modoSelecaoTotal, idsExcecao, idsSelecionados],
  );

  const alternarSelecao = useCallback(
    (id) => {
      if (modoSelecaoTotal) {
        setIdsExcecao((atual) => {
          const novo = new Set(atual);
          if (novo.has(id)) novo.delete(id);
          else novo.add(id);
          return novo;
        });
      } else {
        setIdsSelecionados((atual) => {
          const novo = new Set(atual);
          if (novo.has(id)) novo.delete(id);
          else novo.add(id);
          return novo;
        });
      }
    },
    [modoSelecaoTotal],
  );

  const quantidadeSelecionada = modoSelecaoTotal
    ? Math.max(total - idsExcecao.size, 0)
    : idsSelecionados.size;
  const algumSelecionado = quantidadeSelecionada > 0;
  const tudoSelecionado = modoSelecaoTotal && idsExcecao.size === 0;

  const alternarSelecaoTudo = useCallback(() => {
    if (algumSelecionado) {
      limparSelecao();
    } else {
      setModoSelecaoTotal(true);
      setIdsExcecao(new Set());
    }
  }, [algumSelecionado, limparSelecao]);

  const obterPayload = useCallback(() => {
    if (modoSelecaoTotal) {
      return {
        all: true,
        filter: filterValues,
        exceptIds: Array.from(idsExcecao),
      };
    }
    return { ids: Array.from(idsSelecionados) };
  }, [modoSelecaoTotal, idsExcecao, idsSelecionados, filterValues]);

  return {
    modoSelecaoTotal,
    quantidadeSelecionada,
    algumSelecionado,
    tudoSelecionado,
    estaMarcado,
    alternarSelecao,
    alternarSelecaoTudo,
    limparSelecao,
    obterPayload,
  };
};

/**
 * Checkbox de uma linha do Datagrid. Usado como "Field" customizado (primeira coluna),
 * recebe `record` automaticamente do Datagrid e `estaMarcado`/`onToggle` do componente pai.
 */
const CampoSelecaoLinha = ({ record, estaMarcado, onToggle }) => {
  const classes = useStyles();
  return (
    <Checkbox
      color="primary"
      className={classes.checkboxTabelaCompacto}
      checked={Boolean(record && estaMarcado(record.id))}
      onClick={(event) => {
        event.stopPropagation();
        if (record) onToggle(record.id);
      }}
    />
  );
};

/**
 * Checkbox do cabeçalho da coluna de seleção. Ao marcar, ativa o modo "selecionar todos
 * os N registros do filtro atual" (não só os da página carregada).
 */
const CheckboxCabecalhoSelecao = ({ checked, indeterminate, onClick }) => {
  const classes = useStyles();
  return (
    <Checkbox
      color="primary"
      className={`${classes.checkboxCabecalhoSelecao} ${classes.checkboxTabelaCompacto}`}
      checked={checked}
      indeterminate={indeterminate}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    />
  );
};

/**
 * Campo de busca rápida exibido no cabeçalho, perto do título (antes ficava dentro do
 * drawer "Filtros avançados"). Aplica o filtro ao pressionar Enter ou ao perder o foco —
 * não a cada tecla, pra não disparar uma busca por letra digitada.
 */
const BuscaRapida = ({ source, label, placeholder, debounce = 0 }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const [valor, setValor] = useState(filterValues[source] || '');
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // mantém sincronizado se o filtro for limpo por fora (ex: chip removido, "Limpar tudo")
  useEffect(() => {
    setValor(filterValues[source] || '');
  }, [filterValues, source]);

  // atalho Ctrl/Cmd+K foca a busca rápida a partir de qualquer lugar da tela
  useEffect(() => {
    const handleAtalho = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleAtalho);
    return () => document.removeEventListener('keydown', handleAtalho);
  }, []);

  // limpa o debounce pendente se o componente desmontar (ex: navegação pra outra tela)
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const aplicar = (valorAtual = valor) => {
    const novosFiltros = { ...filterValues };
    if (valorAtual) novosFiltros[source] = valorAtual;
    else delete novosFiltros[source];
    setFilters(novosFiltros, displayedFilters);
  };

  const handleChange = (event) => {
    const novoValor = event.target.value;
    setValor(novoValor);

    // com `debounce` (ms) informado, busca automaticamente enquanto digita, sem esperar
    // Enter/blur — sem ele (padrão), só busca ao pressionar Enter ou ao sair do campo.
    if (debounce > 0) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => aplicar(novoValor), debounce);
    }
  };

  return (
    <TextField
      variant="outlined"
      size="small"
      inputRef={inputRef}
      placeholder={placeholder || label}
      value={valor}
      onChange={handleChange}
      onBlur={() => aplicar()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') aplicar();
      }}
      className={`${classes.buscaRapidaInput} ${
        isMobile ? classes.buscaRapidaInputMobile : ''
      }`}
      InputProps={{
        startAdornment: (
          <SearchIcon
            color="disabled"
            fontSize="small"
            style={isMobile ? { marginRight: 8 } : undefined}
          />
        ),
        // Celular: botão para limpar o texto da busca.
        endAdornment:
          isMobile && valor ? (
            <IconButton
              size="small"
              aria-label="Limpar busca"
              onClick={() => {
                setValor('');
                aplicar('');
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : undefined,
      }}
    />
  );
};

/**
 * Breadcrumb ("Home > ... > título atual") + título/subtítulo da página + busca rápida +
 * botões de ação do outro lado (criar + qualquer outro botão extra via headerActions).
 *
 * breadcrumb: array de strings com os níveis intermediários (sem "Home" e sem o
 * título atual, que são adicionados automaticamente).
 *
 * showBreadcrumb: quando `false`, não renderiza a linha de breadcrumb (só o
 * título/subtítulo e os botões).
 *
 * quickFilterSource/Label/Placeholder: quando `quickFilterSource` é informado, mostra o
 * campo de busca rápida (BuscaRapida) entre o título e os botões de ação.
 *
 * headerActions: elemento(s) extra renderizados antes do botão de criar (ex: outro
 * <Button>, um <Fragment> com vários botões, etc.).
 */
const CabecalhoPagina = ({
  title,
  subtitle,
  breadcrumb,
  showBreadcrumb,
  quickFilterSource,
  quickFilterLabel,
  quickFilterPlaceholder,
  quickFilterDebounce,
  onCreate,
  createLabel,
  headerActions,
}) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const botaoCriarCelular = (
    <Button
      variant="contained"
      size="small"
      aria-label={createLabel}
      className={`${classes.botaoCriar} ${classes.botaoCriarMobile}`}
      onClick={onCreate}
    >
      <AddIcon fontSize="small" />
    </Button>
  );

  return (
    <>
      {showBreadcrumb && (
        <Breadcrumbs className={classes.breadcrumb}>
          <Link color="inherit" href="#/">
            Home
          </Link>
          {breadcrumb.map((item) => (
            <Typography key={item} color="textSecondary">
              {item}
            </Typography>
          ))}
          <Typography color="textPrimary">{title}</Typography>
        </Breadcrumbs>
      )}

      <Box
        className={`${classes.cabecalhoWrapper} ${
          isMobile ? classes.cabecalhoWrapperMobile : ''
        }`}
      >
        <Box className={isMobile ? classes.cabecalhoTituloLinhaMobile : ''}>
          <Box>
            <Typography variant="h4" className={classes.cabecalhoTitulo}>
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body1"
                className={classes.cabecalhoSubtitulo}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {/* Celular: as ações da tela (ex.: resumo) e o de criar ficam ao lado do título. */}
          {isMobile && (headerActions || onCreate) && (
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {headerActions}
              {onCreate && botaoCriarCelular}
            </Box>
          )}
        </Box>
        <Box
          className={`${classes.cabecalhoLadoDireito} ${
            isMobile ? classes.cabecalhoLadoDireitoMobile : ''
          }`}
        >
          {quickFilterSource && (
            <BuscaRapida
              source={quickFilterSource}
              label={quickFilterLabel}
              placeholder={quickFilterPlaceholder}
              debounce={quickFilterDebounce}
            />
          )}
          {/* Sem ações no celular, nem renderiza a caixa: vazia, ela ainda ocupava o espaço do
              "gap" e a busca terminava antes da borda direita (e do botão de criar). */}
          {!isMobile && (headerActions || onCreate) && (
            <Box
              className={`${classes.cabecalhoAcoes} ${
                isMobile ? classes.cabecalhoAcoesMobile : ''
              }`}
            >
              {headerActions}
              {!isMobile && onCreate && (
                <Button
                  variant="contained"
                  size="small"
                  className={classes.botaoCriar}
                  startIcon={<AddIcon />}
                  onClick={onCreate}
                >
                  {createLabel}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

// ações cujo key/label indicam algo destrutivo recebem o estilo vermelho (botaoAcaoSelecaoPerigo)
const ACAO_PERIGOSA_REGEX = /exclu|delet/i;
const isAcaoPerigosa = (acao) =>
  ACAO_PERIGOSA_REGEX.test(acao.key || acao.label || '');

/**
 * Mostra "X selecionados" + as ações em massa, quando há algo selecionado: as primeiras
 * `maxAcoesVisiveis` aparecem como botões individuais, e o restante (se houver) fica
 * agrupado no dropdown "Mais ações".
 *
 * bulkActions: array de { label, icon, onClick(payload) }, onde payload é:
 *   - { ids: string[] }                                quando a seleção foi feita item a item
 *   - { all: true, filter, exceptIds: string[] }       quando o checkbox do cabeçalho foi
 *     usado pra selecionar todos os registros do filtro atual (com excecoes desmarcadas
 *     manualmente). Esse formato ainda não tem suporte no backend — quem implementar o
 *     onClick precisa decidir o que fazer com ele por enquanto.
 */
const AcoesSelecionados = ({ bulkActions, selecao, maxAcoesVisiveis = 4 }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState(null);

  if (!bulkActions.length || !selecao.algumSelecionado) return null;

  const executarAcao = (acao) => {
    acao.onClick(selecao.obterPayload());
    setAnchorEl(null);
    selecao.limparSelecao();
  };

  // no mobile, nenhuma ação fica "fixa" do lado — todas (mesmo as que normalmente
  // apareceriam como botão) ficam dentro do "Mais ações", pra caber na tela.
  const limiteEfetivo = isMobile ? 0 : maxAcoesVisiveis;
  const acoesVisiveis = bulkActions.slice(0, limiteEfetivo);
  const acoesNoMenu = bulkActions.slice(limiteEfetivo);

  return (
    <Box
      className={`${classes.linhaSelecao} ${
        isMobile ? classes.linhaSelecaoMobile : ''
      }`}
    >
      <Box
        className={`${classes.linhaSelecaoLado} ${
          isMobile ? classes.linhaSelecaoLadoMobile : ''
        }`}
      >
        {isMobile ? (
          <Box className={classes.linhaSelecaoTextoEBotaoMobile}>
            <Typography
              variant="body2"
              className={`${classes.acoesSelecionadosTexto} ${classes.acoesSelecionadosTextoMobile}`}
            >
              {`${selecao.quantidadeSelecionada} itens selecionados`}
            </Typography>
            <Button
              size="small"
              className={`${classes.botaoLimparSelecao} ${classes.botaoLimparSelecaoMobile}`}
              onClick={selecao.limparSelecao}
            >
              Limpar seleção
            </Button>
          </Box>
        ) : (
          <>
            <Typography
              variant="body2"
              className={classes.acoesSelecionadosTexto}
            >
              {selecao.modoSelecaoTotal
                ? `Todos os ${selecao.quantidadeSelecionada} itens selecionados`
                : `${selecao.quantidadeSelecionada} item${
                    selecao.quantidadeSelecionada > 1 ? 's' : ''
                  } selecionado${selecao.quantidadeSelecionada > 1 ? 's' : ''}`}
            </Typography>
            <Button
              size="small"
              className={classes.botaoLimparSelecao}
              onClick={selecao.limparSelecao}
            >
              Limpar seleção
            </Button>
          </>
        )}
      </Box>
      <Box
        className={`${classes.linhaSelecaoLado} ${
          isMobile ? classes.linhaSelecaoLadoMobile : ''
        }`}
      >
        {acoesVisiveis.map((acao) => (
          <Button
            key={acao.key || acao.label}
            size="small"
            startIcon={acao.icon}
            className={`${classes.botaoAcaoSelecao} ${
              isAcaoPerigosa(acao) ? classes.botaoAcaoSelecaoPerigo : ''
            }`}
            onClick={() => executarAcao(acao)}
          >
            {acao.label}
          </Button>
        ))}
        {acoesNoMenu.length > 0 && (
          <>
            <Button
              size="small"
              startIcon={<MoreVertIcon />}
              endIcon={<ArrowDropDownIcon />}
              className={classes.botaoAcaoSelecao}
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              {isMobile ? 'Ações' : 'Mais ações'}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              {...obterPropsMenuMobile(isMobile, classes)}
            >
              {acoesNoMenu.map((acao, index) => [
                index > 0 && (
                  <Divider key={`${acao.key || acao.label}-divisor`} />
                ),
                <MenuItem
                  key={acao.key || acao.label}
                  onClick={() => executarAcao(acao)}
                >
                  {acao.icon}
                  {acao.label}
                </MenuItem>,
              ])}
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};

/**
 * tabs: [{ id, name, filter }]
 * - filter: objeto que será mesclado em setFilters quando a tab for selecionada
 *
 * advancedFilters: array de elementos de Input do react-admin (TextInput, SelectInput, etc.)
 * renderizados dentro do drawer "Filtros avançados". Cada um precisa de `source` e `label`.
 *
 * quickFilterSource: source do campo de "Busca rápida" exibido no topo do drawer (default: 'q').
 *
 * quickFilterDebounce: tempo em ms para buscar automaticamente enquanto a pessoa digita na
 * busca rápida (debounce). Sem essa prop (ou com 0, o default), a busca só é feita ao
 * pressionar Enter ou ao sair do campo (blur) — sem buscar a cada tecla digitada.
 *
 * modoRolagemTabela: 'scroll' (default) ou 'setas'. Controla como a tabela é navegada
 * horizontalmente quando há mais colunas do que cabem na tela:
 *   - 'scroll': comportamento padrão, rolagem normal (arraste/wheel/trackpad) com a
 *     barra de rolagem nativa visível.
 *   - 'setas': esconde a barra de rolagem nativa (a rolagem por arraste/wheel ainda
 *     funciona) e mostra dois botões (◀ ▶) acima da tabela que rolam um passo
 *     (`tabelaScrollStep`, em px, default 240) por clique, com animação suave.
 *
 * tabelaScrollStep: quantos px cada clique nas setas rola (só usado com
 * `modoRolagemTabela="setas"`).
 *
 * modoOrdenacao: 'colunas' (default) ou 'select'. Controla como a ordenação é feita:
 *   - 'colunas': comportamento padrão do Datagrid — clica no cabeçalho da coluna pra
 *     ordenar por ela (alterna asc/desc a cada clique).
 *   - 'select': os cabeçalhos deixam de ser clicáveis e aparece um select "Ordenar por"
 *     (só com as colunas que têm `sortBy`/`source` e não têm `sortable={false}`) + um
 *     botão pra alternar crescente/decrescente.
 *
 * tituloNaBarra: false não mostra o título da tela na barra do topo (a tela já tem o próprio título).
 * renderCardMobile: (record, { acoes }) => elemento. Só no modo 'cards': a tela desenha o próprio
 *   cartão; `acoes` são as ações do ActionsField já filtradas pelas permissões da view.
 * modoTabelaMobile: 'tabela' (default) ou 'cards'. Só tem efeito no mobile — no desktop a
 * tabela é sempre renderizada normalmente, independente desse valor.
 *   - 'tabela': mantém a mesma tabela do desktop, com rolagem horizontal (ver
 *     `modoRolagemTabela`) quando há mais colunas do que cabem na tela.
 *   - 'cards': cada registro vira um card (sem rolagem horizontal): o primeiro campo
 *     visível (que não seja o de ações) fica em destaque como título do card; os demais
 *     campos visíveis aparecem como linhas "rótulo: valor"; e o campo de ações (Field com
 *     `source`/key "acoes" — mesma convenção usada nas telas que já reaproveitam este
 *     componente) fica num rodapé, se existir. Fixar coluna (`colunasFixadas`) não tem
 *     efeito nesse modo, já que não existem mais "colunas" pra fixar.
 *
 * bulkActions: array de { label, icon, onClick(payload) } exibido no dropdown "Actions list",
 * que só aparece quando há algo selecionado. Ao marcar o checkbox do cabeçalho, em vez de
 * marcar só os ids da página carregada, entra no modo "selecionar todos os N registros do
 * filtro atual" (a pessoa pode desmarcar alguns manualmente depois). Por isso o payload tem
 * dois formatos possíveis — ver comentário de AcoesSelecionados para detalhes.
 *
 * breadcrumb: array de strings com os níveis intermediários do caminho (ex: ['Vendas']),
 * exibido como "Home > Vendas > {title}".
 *
 * subtitle: texto exibido abaixo do título.
 *
 * onCreate: quando informado, exibe um botão de criar (createLabel) do lado direito do título.
 *
 * headerActions: elemento(s) extra renderizados ao lado do botão de criar (ex: outros
 * botões de ação que você queira exibir no topo, junto do título).
 *
 * itemLabelPlural: nome (no plural) dos registros listados, usado na frase
 * "Mostrando de X a Y de Z {itemLabelPlural}" (ex: 'pedidos', 'perfis').
 *
 * maxPaginasVisiveis: quantos quadrados de número de página aparecem, no máximo, na
 * paginação antes de agrupar o restante em "..." (default: 7). Os botões de primeira/
 * última página (◀◀ ▶▶) não entram nessa contagem.
 *
 * views: array de { id, label, colunasVisiveis?, acoes?, bulkActions? } exibido no dropdown
 * "Visualizações". Ao trocar de view:
 *   - colunasVisiveis (array de keys de coluna, ex: ['nome','descricao']) restringe quais
 *     colunas existem nessa view — se omitido, todas ficam disponíveis. As que não estão
 *     na lista nem aparecem mais no menu "Colunas" (não é só desmarcar, a opção desaparece).
 *   - acoes (array de keys, ex: ['editar','excluir']) filtra o que aparece num ActionsField
 *     (ou qualquer Field com prop `actions`) — compara com `acao.key` de cada item de
 *     `actions` (cai pro `title` só se a ação não tiver `key`) — se omitido, mostra todas.
 *   - bulkActions (array de keys, ex: ['ativar']) filtra o que aparece no dropdown
 *     "Actions list" — compara com `acao.key` de cada item da prop `bulkActions` do
 *     componente (cai pro `label` só se a ação não tiver `key`) — se omitido, mostra todas.
 *   - toolbarActions (elemento React, ou `null` pra esconder) substitui inteiramente o
 *     `toolbarActions` padrão da tela enquanto essa view estiver ativa — se a chave nem
 *     existir no objeto da view, usa o padrão da tela.
 * Sem `views`, o dropdown "Visualizações" não aparece.
 *
 * colunasFixadasIniciais: mapa { [chave]: 'esquerda' | 'direita' } de colunas que já abrem
 * fixadas (a pessoa ainda pode desfixar). Default `{}`: nenhuma, como sempre foi.
 *
 * filtrosAcimaDasAbas: quando `true` (default), a faixa de filtros ativos fica acima das abas;
 * com `false`, fica abaixo delas.
 *
 * estiloAbas: 'segmentado' (default: pílula cinza dentro do cartão, com a aba ativa preenchida
 * na cor primária do tema) ou 'padrao' (abas com linha azul, cartão separado).
 *
 * showBreadcrumb: exibe "Home > título" no topo (default `false`).
 *
 * larguraFixadaEsquerda / larguraFixadaDireita: largura em px de cada coluna fixada em cada
 * lado (default 220, como sempre foi). Com mais de uma fixada no mesmo lado, cada uma ocupa
 * essa largura.
 *
 * quebrarLinhaColunaFixada: quando `true` (default), texto maior que a coluna fixada quebra
 * em mais linhas em vez de ser cortado com "...".
 *
 * indicarMaisColunas: quando `true` (default), avisa que existem colunas fora da tela — uma
 * sombra na borda por onde a tabela ainda rola, o texto "Há mais colunas" ao lado das setas e
 * as setas em destaque só no sentido em que dá pra rolar.
 *
 * children: elemento (Datagrid) renderizado dentro do Card, abaixo das tabs.
 * Recebe via clone as props listProps, classes e listContext (mesmas que renderGrid recebia),
 * além de ter suas colunas e ações filtradas conforme o menu "Colunas" e a view atual.
 */
const ListPageTabbedComBuscaPagina = ({
  title,
  subtitle,
  breadcrumb = [],
  showBreadcrumb = false,
  onCreate,
  createLabel = 'Novo',
  headerActions = null,
  perPage = 50,
  rowsPerPageOptions = [10, 25, 50, 100],
  itemLabelPlural = 'registros',
  maxPaginasVisiveis = 7,
  toolbarActions = null,
  tabs = [],
  advancedFilters = [],
  quickFilterSource = 'q',
  quickFilterLabel = 'Busca rápida',
  quickFilterPlaceholder = 'Buscar...',
  quickFilterDebounce = 0,
  modoRolagemTabela = 'scroll',
  tabelaScrollStep = 240,
  modoOrdenacao = 'colunas',
  modoTabelaMobile = 'tabela',
  renderCardMobile,
  tituloNaBarra = true,
  bulkActions = [],
  views = [],
  colunasFixadasIniciais = {},
  indicarMaisColunas = true,
  filtrosAcimaDasAbas = true,
  estiloAbas = 'segmentado',
  larguraFixadaEsquerda = PIN_WIDTH,
  larguraFixadaDireita = PIN_WIDTH,
  quebrarLinhaColunaFixada = true,
  children,
  listProps = {},
  ...rest
}) => {
  const classes = useStyles();

  return (
    <ListBase perPage={perPage} {...listProps}>
      <InnerTabList
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumb}
        showBreadcrumb={showBreadcrumb}
        onCreate={onCreate}
        createLabel={createLabel}
        headerActions={headerActions}
        rowsPerPageOptions={rowsPerPageOptions}
        itemLabelPlural={itemLabelPlural}
        maxPaginasVisiveis={maxPaginasVisiveis}
        toolbarActions={toolbarActions}
        tabs={tabs}
        advancedFilters={advancedFilters}
        quickFilterSource={quickFilterSource}
        quickFilterLabel={quickFilterLabel}
        quickFilterPlaceholder={quickFilterPlaceholder}
        quickFilterDebounce={quickFilterDebounce}
        modoRolagemTabela={modoRolagemTabela}
        tabelaScrollStep={tabelaScrollStep}
        modoOrdenacao={modoOrdenacao}
        modoTabelaMobile={modoTabelaMobile}
        renderCardMobile={renderCardMobile}
        tituloNaBarra={tituloNaBarra}
        bulkActions={bulkActions}
        views={views}
        colunasFixadasIniciais={colunasFixadasIniciais}
        indicarMaisColunas={indicarMaisColunas}
        filtrosAcimaDasAbas={filtrosAcimaDasAbas}
        estiloAbas={estiloAbas}
        larguraFixadaEsquerda={larguraFixadaEsquerda}
        larguraFixadaDireita={larguraFixadaDireita}
        quebrarLinhaColunaFixada={quebrarLinhaColunaFixada}
        classes={classes}
        {...rest}
      >
        {children}
      </InnerTabList>
    </ListBase>
  );
};

const InnerTabList = ({
  title,
  subtitle,
  breadcrumb,
  showBreadcrumb,
  onCreate,
  createLabel,
  headerActions,
  rowsPerPageOptions,
  itemLabelPlural,
  maxPaginasVisiveis,
  toolbarActions,
  tabs,
  advancedFilters,
  quickFilterSource,
  quickFilterLabel,
  quickFilterPlaceholder,
  quickFilterDebounce,
  modoRolagemTabela,
  tabelaScrollStep,
  modoOrdenacao,
  modoTabelaMobile,
  renderCardMobile,
  tituloNaBarra,
  bulkActions,
  views,
  colunasFixadasIniciais,
  indicarMaisColunas,
  filtrosAcimaDasAbas,
  estiloAbas,
  larguraFixadaEsquerda,
  larguraFixadaDireita,
  quebrarLinhaColunaFixada,
  classes,
  children,
  ...props
}) => {
  const isMobile = useIsMobile();
  // no modo cards (mobile), não existe mais "coluna" pra fixar nem pra rolar — esconde
  // essas duas opções, que só fazem sentido com a tabela.
  const modoCardsMobile = isMobile && modoTabelaMobile === 'cards';
  const listContext = useListContext();
  const {
    filterValues = {},
    setFilters,
    displayedFilters,
    total = 0,
    loaded,
    currentSort,
    setSort,
  } = listContext || {};
  // só considera "sem dados" depois que a primeira busca terminou — `total` também é 0
  // enquanto a página carrega, e não queremos piscar a mensagem de vazio nesse meio tempo.
  const semDados = loaded && total === 0;

  const history = useHistory();
  const location = useLocation();

  // usado só quando `modoRolagemTabela="setas"`, pra rolar a tabela programaticamente
  // a partir dos botões (ver NavegacaoSetasTabela mais abaixo).
  const tabelaScrollRef = useRef(null);
  const cabecalhoFixoRef = useRef(null);
  const barraFixoRef = useRef(null);
  // Abas presas no topo ao rolar: a caixa que as reserva e a altura que ocupam (as ferramentas e o
  // cabeçalho da tabela, que também ficam presos, descem essa altura).
  const abasFixasRef = useRef(null);
  const alturaAbasPresas = useRef(0);
  const rolarTabela = useCallback(
    (direcao) => {
      tabelaScrollRef.current?.scrollBy({
        left: direcao * tabelaScrollStep,
        behavior: 'smooth',
      });
    },
    [tabelaScrollStep],
  );

  const [drawerAberto, setDrawerAberto] = useState(false);
  const colunas = useMemo(() => extrairColunas(children), [children]);
  // a view selecionada é guardada em estado (não só lida direto da URL): o react-admin
  // reescreve a query string inteira (sem o nosso `?view=`) sempre que a pessoa troca de
  // página, muda o "por página" ou aplica um filtro — isso fazia a view voltar pra padrão
  // nessas ações. O estado sobrevive a isso; o efeito abaixo mantém a URL sincronizada
  // (pra continuar funcionando com refresh, link compartilhado e voltar/avançar).
  const [viewIdSelecionado, setViewIdSelecionado] = useState(
    () => new URLSearchParams(location.search).get('view') || null,
  );
  const viewAtual = useMemo(
    () => views.find((view) => view.id === viewIdSelecionado) || views[0],
    [views, viewIdSelecionado],
  );
  useEffect(() => {
    const viewNaUrl = new URLSearchParams(location.search).get('view');
    if (viewNaUrl === viewIdSelecionado) return;
    if (viewNaUrl) {
      // navegação direta por link com ?view=..., ou voltar/avançar do navegador.
      setViewIdSelecionado(viewNaUrl);
    } else if (viewIdSelecionado) {
      // alguma navegação do react-admin (página, per page, filtro) removeu o `view` da
      // URL sem querer — devolve ele, sem empilhar uma entrada no histórico.
      const params = new URLSearchParams(location.search);
      params.set('view', viewIdSelecionado);
      history.replace({ search: params.toString() });
    }
  }, [location.search, viewIdSelecionado, history]);
  const [colunasVisiveis, setColunasVisiveis] = useState(() =>
    construirVisibilidadeInicial(viewAtual, colunas),
  );
  // recalcula as colunas visíveis sempre que a view mudar (inclusive via navegação,
  // não só quando a pessoa troca pelo menu "Visualizações").
  useEffect(() => {
    setColunasVisiveis(construirVisibilidadeInicial(viewAtual, colunas));
  }, [viewAtual, colunas]);
  // colunas que a view atual permite nem sequer oferecer no menu "Colunas" — se a view não
  // restringir (colunasVisiveis indefinido), mostra todas como hoje.
  const colunasDisponiveis = useMemo(() => {
    if (!viewAtual || !Array.isArray(viewAtual.colunasVisiveis)) return colunas;
    const permitidas = new Set(viewAtual.colunasVisiveis);
    return colunas.filter((coluna) => permitidas.has(coluna.key));
  }, [colunas, viewAtual]);
  // colunas ordenáveis (com `sortBy`/`source` e sem `sortable={false}`), usadas pelo
  // select de ordenação quando `modoOrdenacao="select"`.
  const colunasOrdenaveis = useMemo(
    () => colunasDisponiveis.filter((coluna) => coluna.sortable),
    [colunasDisponiveis],
  );
  // com `modoOrdenacao="select"`, o SeletorOrdenacao só consegue mostrar uma das colunas
  // ordenáveis como selecionada — se o sort atual (ex: o `field: 'id'` inicial da tela)
  // não for nenhuma delas, corrige pra primeira coluna ordenável. Sem isso, o select fica
  // mostrando uma coluna como "selecionada" que não é a que está realmente ordenando, e
  // selecionar essa mesma coluna de novo não dispara nada (pro select, nada mudou).
  useEffect(() => {
    if (modoOrdenacao !== 'select' || !colunasOrdenaveis.length || !setSort) {
      return;
    }
    const sortAtualValido = colunasOrdenaveis.some(
      (coluna) => coluna.sortBy === currentSort?.field,
    );
    if (!sortAtualValido) {
      setSort(colunasOrdenaveis[0].sortBy, currentSort?.order || 'ASC');
    }
  }, [
    modoOrdenacao,
    colunasOrdenaveis,
    currentSort?.field,
    currentSort?.order,
    setSort,
  ]);
  // o dropdown "Actions list" (bulkActions) também respeita a view atual — se ela definir
  // `bulkActions` (array de keys), só essas ações aparecem; sem isso, mostra todas.
  // Identifica cada ação pelo `key` (cai pro `label` se não tiver, mas prefira definir `key`).
  const bulkActionsDisponiveis = useMemo(() => {
    if (!viewAtual || !Array.isArray(viewAtual.bulkActions)) return bulkActions;
    const permitidas = new Set(viewAtual.bulkActions);
    return bulkActions.filter((acao) => permitidas.has(acao.key || acao.label));
  }, [bulkActions, viewAtual]);
  // se a view atual definir seu próprio toolbarActions (mesmo que `null`, pra esconder),
  // ele substitui o padrão da tela. Por isso o check é com `in`, não com `??`/`||`.
  const toolbarActionsExibidas =
    viewAtual && 'toolbarActions' in viewAtual
      ? viewAtual.toolbarActions
      : toolbarActions;
  const selecionarView = useCallback(
    (id) => {
      setViewIdSelecionado(id);
      const params = new URLSearchParams(location.search);
      params.set('view', id);
      // replace (não push) — trocar de view não deveria empilhar entradas no histórico
      // do navegador a cada clique.
      history.replace({ search: params.toString() });
    },
    [location.search, history],
  );
  const selecao = useSelecaoEmMassa(total, filterValues);

  // encontra tab ativa pela comparação dos filtros
  const activeTab = tabs.find((t) => isFilterMatch(t.filter, filterValues)) ||
    tabs[0] || { id: 'all', name: 'All', filter: {} };
  const activeId = activeTab.id;

  const handleChange = useCallback(
    (event, tabId) => {
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab) return;
      const newFilters = { ...(filterValues || {}), ...(tab.filter || {}) };
      if (setFilters) setFilters(newFilters, displayedFilters);
    },
    [tabs, filterValues, setFilters, displayedFilters],
  );

  const toggleColuna = useCallback((chave) => {
    setColunasVisiveis((atual) => ({
      ...atual,
      [chave]: atual[chave] === false,
    }));
  }, []);
  // marca todas as colunas disponíveis como visíveis de uma vez (botão "Marcar todas").
  const marcarTodasColunas = useCallback(() => {
    setColunasVisiveis({});
  }, []);

  // colunasFixadas: mapa { [chave]: 'esquerda' | 'direita' }. Clicar de novo no mesmo lado
  // desfixa (toggle); clicar no lado oposto troca de lado direto.
  const [colunasFixadas, setColunasFixadas] = useState(colunasFixadasIniciais);
  // há mais colunas fora da tela à esquerda/direita (só calculado com `indicarMaisColunas`).
  const [bordasRolagem, setBordasRolagem] = useState({
    esquerda: false,
    direita: false,
  });
  const fixarColuna = useCallback((chave, lado) => {
    setColunasFixadas((atual) => {
      const novo = { ...atual };
      if (novo[chave] === lado) delete novo[chave];
      else novo[chave] = lado;
      return novo;
    });
  }, []);
  // remove a fixação de todas as colunas de uma vez (botão "Desfixar todas").
  const desfixarTodasColunas = useCallback(() => {
    setColunasFixadas({});
  }, []);
  // perde sentido manter colunas fixadas que a view atual nem oferece mais (ex: trocou
  // pra uma view que restringe `colunasVisiveis` e não inclui mais essa coluna).
  useEffect(() => {
    setColunasFixadas((atual) => {
      const chavesPermitidas = new Set(colunasDisponiveis.map((c) => c.key));
      const novo = Object.fromEntries(
        Object.entries(atual).filter(([chave]) => chavesPermitidas.has(chave)),
      );
      return Object.keys(novo).length === Object.keys(atual).length
        ? atual
        : novo;
    });
  }, [colunasDisponiveis]);

  const childrenComColunasFiltradas = useMemo(() => {
    const filhos = React.Children.toArray(children?.props?.children || []);
    const filhosVisiveis = filhos
      .filter((filho, index) => {
        const chave = obterChaveColuna(filho, index);
        return colunasVisiveis[chave] !== false;
      })
      .map((filho) => aplicarFiltroDeAcoes(filho, viewAtual?.acoes))
      // com `modoOrdenacao="select"`, a ordenação é feita só pelo SeletorOrdenacao — os
      // cabeçalhos ficam sem o clique/indicador de ordenação, pra não ter dois jeitos
      // diferentes de fazer a mesma coisa ao mesmo tempo.
      .map((filho) =>
        modoOrdenacao === 'select'
          ? React.cloneElement(filho, { sortable: false })
          : filho,
      )
      .map(omitirPropMobile);

    // reordena pra trazer as colunas fixadas à esquerda pro início (na ordem em que
    // aparecem) e empurra as fixadas à direita pro fim — igual ao comportamento usual de
    // "pin column" em outras ferramentas (a coluna pinada move pra junto da borda).
    const fixadasEsquerda = [];
    const fixadasDireita = [];
    const semFixacao = [];
    filhosVisiveis.forEach((filho, index) => {
      const chave = obterChaveColuna(filho, index);
      if (colunasFixadas[chave] === 'esquerda') fixadasEsquerda.push(filho);
      else if (colunasFixadas[chave] === 'direita') fixadasDireita.push(filho);
      else semFixacao.push(filho);
    });
    // Sem bulkActions não existe coluna de seleção (checkbox) reservando espaço na borda
    // esquerda — nesse caso o deslocamento `left` das colunas fixadas à esquerda não pode
    // contar com esse espaço (ver `estilosColunaFixadaEsquerdaSemSelecao`), senão a 1a
    // coluna fixada sobrepõe a coluna seguinte em vez de ficar ao lado dela.
    const temSelecaoNaTabela = bulkActions.length > 0;
    // aplica a classe sticky (com o deslocamento correspondente à posição na pilha de
    // colunas fixadas daquele lado) via headerClassName/cellClassName do próprio field.
    const aplicarClasseFixacao = (filho, indiceNaPilha, lado) => {
      const headerClasse =
        lado === 'esquerda'
          ? classes[
              temSelecaoNaTabela
                ? `colunaFixadaEsquerda${indiceNaPilha}`
                : `colunaFixadaEsquerdaSemSelecao${indiceNaPilha}`
            ]
          : classes[`colunaFixadaDireita${indiceNaPilha}`];
      if (!headerClasse) return filho; // além de MAX_COLUNAS_FIXADAS, não fixa mais
      return React.cloneElement(filho, {
        headerClassName: [filho.props.headerClassName, headerClasse]
          .filter(Boolean)
          .join(' '),
        cellClassName: [
          filho.props.cellClassName,
          headerClasse,
          classes.colunaFixadaCelula,
          quebrarLinhaColunaFixada && classes.colunaFixadaQuebra,
        ]
          .filter(Boolean)
          .join(' '),
      });
    };
    const fixadasEsquerdaComClasse = fixadasEsquerda.map((filho, i) =>
      aplicarClasseFixacao(filho, i, 'esquerda'),
    );
    // a última coluna fixada à direita (mais próxima da borda) fica com offset 0 —
    // por isso a pilha é montada de trás pra frente.
    const fixadasDireitaComClasse = fixadasDireita
      .slice()
      .reverse()
      .map((filho, i) => aplicarClasseFixacao(filho, i, 'direita'))
      .reverse();

    const filhosReordenados = [
      ...fixadasEsquerdaComClasse,
      ...semFixacao,
      ...fixadasDireitaComClasse,
    ];
    // quando há colunas fixadas à esquerda, a coluna de seleção também fica fixa (em left:0)
    // pra continuar visível junto delas — sem isso, ela rolaria pra fora normalmente.
    const temFixacaoEsquerda = fixadasEsquerda.length > 0;

    // a coluna de seleção é nossa (não a do Datagrid padrão), pra suportar o modo
    // "selecionar todos os N registros do filtro" — por isso hasBulkActions fica sempre false.
    const colunaSelecao = temSelecaoNaTabela && (
      <CampoSelecaoLinha
        key="__selecao"
        source="__selecao"
        sortable={false}
        headerClassName={[
          classes.colunaSelecaoCelula,
          temFixacaoEsquerda && classes.colunaSelecaoFixada,
        ]
          .filter(Boolean)
          .join(' ')}
        cellClassName={[
          classes.colunaSelecaoCelula,
          temFixacaoEsquerda && classes.colunaSelecaoFixada,
          temFixacaoEsquerda && classes.colunaFixadaCelula,
        ]
          .filter(Boolean)
          .join(' ')}
        label={
          <CheckboxCabecalhoSelecao
            checked={selecao.tudoSelecionado}
            indeterminate={selecao.algumSelecionado && !selecao.tudoSelecionado}
            onClick={selecao.alternarSelecaoTudo}
          />
        }
        estaMarcado={selecao.estaMarcado}
        onToggle={selecao.alternarSelecao}
      />
    );
    return React.cloneElement(children, {
      // o DatagridHeaderCell/DatagridCell do react-admin são memoizados com comparadores
      // que ignoram boa parte das props (ex: o do header só olha sort/resource) — então
      // mudanças de seleção ou de colunas visíveis podem não refletir visualmente mesmo
      // com os filhos certos sendo passados. Forçamos a remontagem da tabela nessas
      // transições mudando a key, o que contorna esses bail-outs de memo.
      key: `${selecao.algumSelecionado}-${
        selecao.tudoSelecionado
      }-${Object.keys(colunasVisiveis)
        .map((chave) => `${chave}:${colunasVisiveis[chave]}`)
        .join(',')}-${Object.keys(colunasFixadas)
        .map((chave) => `${chave}:${colunasFixadas[chave]}`)
        .join(',')}`,
      listProps: props,
      // preserva o `classes` próprio do Datagrid (ex: classes={{ headerCell: ... }})
      // quando informado pela tela; só usa o `classes` interno deste componente como fallback.
      classes: children.props.classes || classes,
      // fundo sólido: a célula da coluna fixada herda a cor da linha, e sem ela o conteúdo que
      // rola por baixo apareceria através da coluna fixa.
      rowStyle: children.props.rowStyle || linhaOpaca,
      listContext,
      hasBulkActions: false,
      children: colunaSelecao
        ? [colunaSelecao, ...filhosReordenados]
        : filhosReordenados,
    });
  }, [
    children,
    colunasVisiveis,
    colunasFixadas,
    modoOrdenacao,
    viewAtual,
    props,
    classes,
    listContext,
    bulkActions,
    selecao,
    quebrarLinhaColunaFixada,
  ]);

  useEffect(() => {
    const el = tabelaScrollRef.current;
    if (!indicarMaisColunas || !el) return undefined;
    const atualizar = () => {
      const esquerda = el.scrollLeft > 1;
      const direita = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setBordasRolagem((atual) =>
        atual.esquerda === esquerda && atual.direita === direita
          ? atual
          : { esquerda, direita },
      );
    };
    atualizar();
    el.addEventListener('scroll', atualizar, { passive: true });
    window.addEventListener('resize', atualizar);
    // a largura da tabela muda quando os dados chegam ou as colunas mudam.
    const observador =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(atualizar)
        : null;
    if (observador) {
      observador.observe(el);
      if (el.firstElementChild) observador.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener('scroll', atualizar);
      window.removeEventListener('resize', atualizar);
      if (observador) observador.disconnect();
    };
  }, [indicarMaisColunas, childrenComColunasFiltradas, modoCardsMobile]);

  // Abas presas logo abaixo da barra do topo enquanto a lista rola. O Card tem `overflow: hidden`
  // (o que impede `position: sticky`), então a faixa vira `position: fixed` quando sai de vista,
  // acompanhando a barra do topo (que some ao rolar para baixo e volta ao rolar para cima).
  const temAbasFixas = Array.isArray(tabs) && tabs.length > 0;
  // Celular com cartões: não há tabela larga, então o Card não precisa cortar o que passa da
  // borda e as abas usam `position: sticky` (descendo a altura da barra do topo enquanto ela
  // está visível). Nos demais casos vale a posição fixa calculada no efeito abaixo.
  const abasStickyCss = modoCardsMobile;
  const barraEscondida = useScrollTrigger();
  const telaMuitoPequena = useMediaQuery((tema) => tema.breakpoints.down('xs'));
  const alturaBarraTopo = telaMuitoPequena ? 56 : 48;
  useEffect(() => {
    const caixa = abasStickyCss ? null : abasFixasRef.current;
    const faixa = caixa && caixa.firstElementChild;
    const cartao = caixa && caixa.parentElement;
    if (!caixa || !faixa) return undefined;
    const posicionar = () => {
      const barra = document.querySelector('header.MuiAppBar-root');
      const topo = barra
        ? Math.max(0, barra.getBoundingClientRect().bottom)
        : 0;
      if (faixa.style.position !== 'fixed') {
        caixa.style.minHeight = `${faixa.offsetHeight}px`;
      }
      const natural = caixa.getBoundingClientRect();
      const fim = cartao ? cartao.getBoundingClientRect().bottom : Infinity;
      const presa = natural.top < topo && fim - natural.height > topo;
      Object.assign(faixa.style, {
        position: presa ? 'fixed' : '',
        top: presa ? `${topo}px` : '',
        left: presa ? `${natural.left}px` : '',
        width: presa ? `${natural.width}px` : '',
        zIndex: presa ? 7 : '',
        backgroundColor: presa ? '#fff' : '',
        boxSizing: presa ? 'border-box' : '',
      });
      alturaAbasPresas.current = presa ? natural.height : 0;
    };
    posicionar();
    window.addEventListener('scroll', posicionar, true);
    window.addEventListener('resize', posicionar);
    return () => {
      window.removeEventListener('scroll', posicionar, true);
      window.removeEventListener('resize', posicionar);
      faixa.style.position = '';
      alturaAbasPresas.current = 0;
    };
  }, [temAbasFixas, abasStickyCss]);

  // header preso na janela: o wrapper da tabela rola na horizontal (então um `sticky` do
  // próprio header não vale pra janela). Quando o header original sai de vista pelo topo,
  // aparece uma cópia dele (`position: fixed`) cuja rolagem horizontal acompanha a da tabela.
  // Cliques na cópia são repassados ao elemento equivalente do header original.
  useEffect(() => {
    const wrapper = tabelaScrollRef.current;
    const fixo = cabecalhoFixoRef.current;
    const thead = wrapper && wrapper.querySelector('thead');
    const tabela = thead && thead.closest('table');
    if (!fixo || !tabela) return undefined;
    const copia = document.createElement('table');
    copia.className = tabela.className;
    copia.appendChild(thead.cloneNode(true));
    fixo.replaceChildren(copia);
    const ths = Array.from(thead.querySelectorAll('th'));
    const cths = Array.from(copia.querySelectorAll('th'));

    const medir = () => {
      copia.style.width = `${tabela.offsetWidth}px`;
      ths.forEach((th, i) => {
        const largura = `${th.getBoundingClientRect().width}px`;
        cths[i].style.width = largura;
        cths[i].style.minWidth = largura;
        cths[i].style.maxWidth = largura;
        cths[i].style.boxSizing = 'border-box';
      });
    };
    const posicionar = () => {
      const barra = document.querySelector('header.MuiAppBar-root');
      // Barra do topo + abas presas: as ferramentas e o cabeçalho ficam logo abaixo delas.
      const topo =
        (barra ? Math.max(0, barra.getBoundingClientRect().bottom) : 0) +
        alturaAbasPresas.current;
      const area = tabela.getBoundingClientRect();
      const altura = thead.offsetHeight;
      // a linha de ferramentas (colunas, setas...) também fica presa: o container dela
      // reserva a altura no fluxo e a linha em si vira `position: fixed`.
      const barraBox = barraFixoRef.current;
      const barraEl = barraBox && barraBox.firstElementChild;
      let alturaBarra = 0;
      if (barraEl) {
        if (barraEl.style.position !== 'fixed') {
          barraBox.style.minHeight = `${barraEl.offsetHeight}px`;
        }
        const natural = barraBox.getBoundingClientRect();
        const presa =
          natural.top < topo && area.bottom - altura - natural.height > topo;
        Object.assign(barraEl.style, {
          position: presa ? 'fixed' : '',
          top: presa ? `${topo}px` : '',
          left: presa ? `${natural.left}px` : '',
          width: presa ? `${natural.width}px` : '',
          zIndex: presa ? 6 : '',
          backgroundColor: presa ? '#fff' : '',
          boxSizing: presa ? 'border-box' : '',
          borderBottom: presa ? '1px solid #e2e8f0' : '',
        });
        if (presa) alturaBarra = natural.height;
      }
      const topoCabecalho = topo + alturaBarra;
      const visivel =
        area.top < topoCabecalho && area.bottom - altura > topoCabecalho;
      fixo.style.display = visivel ? 'block' : 'none';
      if (!visivel) return;
      fixo.style.top = `${topoCabecalho}px`;
      fixo.style.left = `${wrapper.getBoundingClientRect().left}px`;
      fixo.style.width = `${wrapper.clientWidth}px`;
      fixo.scrollLeft = wrapper.scrollLeft;
    };
    const atualizar = () => {
      medir();
      posicionar();
    };
    const repassarClique = (evento) => {
      const indice = cths.findIndex((cth) => cth.contains(evento.target));
      if (indice < 0) return;
      const caminho = [];
      let no = evento.target;
      while (no !== cths[indice]) {
        caminho.unshift(
          Array.prototype.indexOf.call(no.parentNode.children, no),
        );
        no = no.parentNode;
      }
      let alvo = ths[indice];
      caminho.forEach((posicao) => {
        alvo = alvo && alvo.children[posicao];
      });
      evento.preventDefault();
      (alvo || ths[indice]).click();
    };

    atualizar();
    fixo.addEventListener('click', repassarClique);
    wrapper.addEventListener('scroll', posicionar, { passive: true });
    window.addEventListener('scroll', posicionar, true);
    window.addEventListener('resize', atualizar);
    const observador =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(atualizar)
        : null;
    if (observador) observador.observe(tabela);
    return () => {
      fixo.removeEventListener('click', repassarClique);
      wrapper.removeEventListener('scroll', posicionar);
      window.removeEventListener('scroll', posicionar, true);
      window.removeEventListener('resize', atualizar);
      if (observador) observador.disconnect();
      fixo.style.display = 'none';
      if (barraFixoRef.current && barraFixoRef.current.firstElementChild) {
        barraFixoRef.current.firstElementChild.style.position = '';
      }
    };
  }, [childrenComColunasFiltradas, modoCardsMobile]);

  // colunas fixadas ocupam as bordas: a sombra começa depois delas.
  const fixadasDeUmLado = (lado) =>
    Object.values(colunasFixadas).filter((valor) => valor === lado).length;
  const larguraFixadasEsquerda =
    fixadasDeUmLado('esquerda') * larguraFixadaEsquerda +
    (bulkActionsDisponiveis.length > 0 ? CHECKBOX_WIDTH : 0);
  const larguraFixadasDireita =
    fixadasDeUmLado('direita') * larguraFixadaDireita;

  const abasNavegador = tabs.some((choice) => choice.cor);
  // Celular, abas no estilo navegador (com cor/contador): usa a faixa própria para celular, que
  // não corta a bolinha do número e avisa quando há mais abas.
  const abasSegmentadas =
    isMobile && abasNavegador ? (
      <AbasNavegador
        valor={activeId}
        onChange={(id) => handleChange(null, id)}
        abas={tabs.map((choice) => ({
          id: choice.id,
          name: choice.name,
          cor: choice.cor || '#64748b',
          icon: choice.icon,
          contador: choice.contador,
        }))}
      />
    ) : (
      <Box
        className={`${classes.abasSegmentadasWrapper} ${
          abasNavegador ? classes.abasNavegadorWrapper : ''
        }`}
      >
        <Tabs
          value={activeId}
          onChange={handleChange}
          className={`${classes.abasSegmentadas} ${
            abasNavegador ? classes.abasNavegador : ''
          }`}
          classes={
            isMobile ? undefined : { scroller: classes.abasSegmentadasScroller }
          }
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : 'off'}
          TabIndicatorProps={{ style: { display: 'none' } }}
        >
          {tabs.map((choice) => {
            const destaque = Boolean(choice.cor);
            const ativa = choice.id === activeId;
            return (
              <Tab
                key={choice.id}
                value={choice.id}
                className={`${classes.abaSegmentada} ${
                  destaque ? classes.abaSegmentadaDestaque : ''
                }`}
                label={
                  destaque || choice.contador != null ? (
                    <>
                      {choice.icon && (
                        <span
                          style={{
                            display: 'inline-flex',
                            color: choice.cor,
                          }}
                        >
                          {choice.icon}
                        </span>
                      )}
                      {choice.name}
                      {choice.contador != null && (
                        <span
                          className={classes.abaBadge}
                          style={{ '--cor-aba': choice.cor || '#64748b' }}
                        >
                          {choice.contador > 9999 ? '9999+' : choice.contador}
                        </span>
                      )}
                    </>
                  ) : (
                    choice.name
                  )
                }
              />
            );
          })}
        </Tabs>
      </Box>
    );

  const filtrosAtivos = (
    <FiltrosAtivos
      advancedFilters={advancedFilters}
      quickFilterSource={quickFilterSource}
      quickFilterLabel={quickFilterLabel}
      chavesDaTabAtiva={Object.keys(activeTab.filter || {})}
      onAbrirFiltrosAvancados={() => setDrawerAberto(true)}
    />
  );

  return (
    <Box className={isMobile ? classes.listaWrapperMobile : undefined}>
      {tituloNaBarra && <Title title={title} />}
      <CabecalhoPagina
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumb}
        showBreadcrumb={showBreadcrumb}
        quickFilterSource={quickFilterSource}
        quickFilterLabel={quickFilterLabel}
        quickFilterPlaceholder={quickFilterPlaceholder}
        quickFilterDebounce={quickFilterDebounce}
        onCreate={onCreate}
        createLabel={createLabel}
        headerActions={headerActions}
      />
      {filtrosAcimaDasAbas && filtrosAtivos}
      {Array.isArray(tabs) && tabs.length > 0 && estiloAbas !== 'segmentado' && (
        <Card className={classes.tabsExterno}>
          <Tabs
            value={activeId}
            onChange={handleChange}
            className={classes.tabs}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : 'off'}
            TabIndicatorProps={{
              style: {
                backgroundColor: '#2563eb',
                height: 3,
              },
            }}
          >
            {tabs.map((choice) => (
              <Tab
                key={choice.id}
                label={choice.name}
                value={choice.id}
                className={classes.tab}
              />
            ))}
          </Tabs>
        </Card>
      )}

      {!filtrosAcimaDasAbas && filtrosAtivos}

      <Card
        className={classes.rootCard}
        style={abasStickyCss ? { overflow: 'visible' } : undefined}
      >
        {estiloAbas === 'segmentado' &&
          temAbasFixas &&
          (abasStickyCss ? (
            <div
              style={{
                position: 'sticky',
                top: barraEscondida ? 0 : alturaBarraTopo,
                transition: 'top 225ms',
                zIndex: 6,
                backgroundColor: '#fff',
                borderRadius: '8px 8px 0 0',
              }}
            >
              {abasSegmentadas}
            </div>
          ) : (
            <div ref={abasFixasRef}>{abasSegmentadas}</div>
          ))}
        <div ref={barraFixoRef}>
          <Box
            className={`${classes.toolbarSecundaria} ${
              isMobile ? classes.toolbarSecundariaMobile : ''
            }`}
          >
            <Box
              className={`${classes.toolbarSecundariaGrupo} ${
                isMobile ? classes.toolbarSecundariaGrupoMobile : ''
              }`}
            >
              {/* Com cartão próprio no celular não há colunas para escolher. */}
              {!(modoCardsMobile && renderCardMobile) && (
                <MenuColunas
                  colunas={colunasDisponiveis}
                  colunasVisiveis={colunasVisiveis}
                  onToggleColuna={toggleColuna}
                  onMarcarTodas={marcarTodasColunas}
                  colunasFixadas={colunasFixadas}
                  onFixarColuna={fixarColuna}
                  onDesfixarTodas={desfixarTodasColunas}
                  permitirFixar={!modoCardsMobile}
                />
              )}
              {views.length > 0 && (
                <MenuVisualizacoes
                  views={views}
                  viewAtualId={viewAtual?.id}
                  onSelecionar={selecionarView}
                />
              )}
              {!isMobile && modoOrdenacao === 'select' && (
                <SeletorOrdenacao
                  colunas={colunasOrdenaveis}
                  currentSort={currentSort}
                  onChangeSort={setSort}
                />
              )}
              {isMobile && (
                <Box className={classes.toolbarActionsWrapper}>
                  {toolbarActionsExibidas}
                </Box>
              )}
            </Box>
            {isMobile && !modoCardsMobile && <Divider />}
            {!modoCardsMobile && (
              <Box
                className={`${classes.toolbarActionsWrapper} ${
                  isMobile ? classes.toolbarActionsWrapperMobile : ''
                }`}
              >
                {!isMobile && toolbarActionsExibidas}
                {isMobile && modoOrdenacao === 'select' && !modoCardsMobile && (
                  <SeletorOrdenacao
                    colunas={colunasOrdenaveis}
                    currentSort={currentSort}
                    onChangeSort={setSort}
                  />
                )}
                {indicarMaisColunas &&
                  !modoCardsMobile &&
                  (bordasRolagem.esquerda || bordasRolagem.direita) && (
                    <Typography
                      variant="caption"
                      color="primary"
                      style={{ marginLeft: 16, fontWeight: 600 }}
                    >
                      {bordasRolagem.direita
                        ? 'Há mais colunas à direita →'
                        : '← Há mais colunas à esquerda'}
                    </Typography>
                  )}
                {modoRolagemTabela === 'setas' && !modoCardsMobile && (
                  <Box
                    className={`${classes.grupoBotoesSetaTabela} ${
                      isMobile ? classes.grupoBotoesSetaTabelaMobile : ''
                    }`}
                  >
                    <IconButton
                      size="small"
                      className={`${classes.botaoSetaTabela} ${
                        isMobile ? classes.botaoSetaTabelaMobile : ''
                      }`}
                      color={bordasRolagem.esquerda ? 'primary' : 'default'}
                      disabled={indicarMaisColunas && !bordasRolagem.esquerda}
                      onClick={() => rolarTabela(-1)}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Divider orientation="vertical" flexItem />
                    <IconButton
                      size="small"
                      className={`${classes.botaoSetaTabela} ${
                        isMobile ? classes.botaoSetaTabelaMobile : ''
                      }`}
                      color={bordasRolagem.direita ? 'primary' : 'default'}
                      disabled={indicarMaisColunas && !bordasRolagem.direita}
                      onClick={() => rolarTabela(1)}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </div>
        {isMobile && <Divider />}
        <AcoesSelecionados
          bulkActions={bulkActionsDisponiveis}
          selecao={selecao}
        />
        <Divider />
        {isMobile && modoTabelaMobile === 'cards' ? (
          !semDados && (
            <ListaCards
              datagridElement={children}
              colunasVisiveis={colunasVisiveis}
              acoesPermitidas={viewAtual?.acoes}
              bulkActions={bulkActionsDisponiveis}
              selecao={selecao}
              modoOrdenacao={modoOrdenacao}
              colunasOrdenaveis={colunasOrdenaveis}
              currentSort={currentSort}
              onChangeSort={setSort}
              renderCard={renderCardMobile}
            />
          )
        ) : (
          <div style={{ position: 'relative' }}>
            <div
              ref={tabelaScrollRef}
              style={{
                '--largura-fixada-esquerda': `${larguraFixadaEsquerda}px`,
                '--largura-fixada-direita': `${larguraFixadaDireita}px`,
              }}
              className={`${classes.tabelaWrapper} ${
                modoRolagemTabela === 'setas'
                  ? classes.tabelaWrapperSemScrollbar
                  : ''
              }`}
            >
              {childrenComColunasFiltradas}
            </div>
            <div
              ref={cabecalhoFixoRef}
              style={{
                '--largura-fixada-esquerda': `${larguraFixadaEsquerda}px`,
                '--largura-fixada-direita': `${larguraFixadaDireita}px`,
              }}
              className={`${classes.tabelaWrapper} ${classes.tabelaWrapperSemScrollbar} ${classes.cabecalhoFixo}`}
            />
            {indicarMaisColunas && bordasRolagem.direita && (
              <div
                className={classes.sombraMaisColunas}
                style={{
                  right: larguraFixadasDireita,
                  background:
                    'linear-gradient(to left, rgba(0,0,0,0.16), rgba(0,0,0,0))',
                }}
              />
            )}
            {indicarMaisColunas && bordasRolagem.esquerda && (
              <div
                className={classes.sombraMaisColunas}
                style={{
                  left: larguraFixadasEsquerda,
                  background:
                    'linear-gradient(to right, rgba(0,0,0,0.16), rgba(0,0,0,0))',
                }}
              />
            )}
          </div>
        )}
        {semDados ? (
          <SemDados itemLabelPlural={itemLabelPlural} />
        ) : (
          <PaginacaoComBusca
            rowsPerPageOptions={rowsPerPageOptions}
            itemLabelPlural={itemLabelPlural}
            maxPaginasVisiveis={maxPaginasVisiveis}
          />
        )}
      </Card>

      <DrawerFiltrosAvancados
        open={drawerAberto}
        onClose={() => setDrawerAberto(false)}
        advancedFilters={advancedFilters}
        filtrosFixos={activeTab.filter}
      />
    </Box>
  );
};

export default ListPageTabbedComBuscaPagina;
